/**
 * Typed GraphQL client for CDK-managed models.
 * Mirrors the Amplify dataClient.models.X.verb() API so existing
 * code can switch from `dataClient.models.Foo` to `cdkDataClient.models.Foo`
 * with minimal changes.
 */

import { generateClient } from 'aws-amplify/api';
import 'react-native-get-random-values';
import { v4 as uuidv4 } from 'uuid';

/**
 * Auto-generate an 'id' field if not provided in the input.
 * CDK-managed models require an explicit id (unlike Amplify a.model() which auto-generated).
 */
function ensureId<T extends Record<string, any>>(input: T): T & { id: string } {
  if (input.id) return input as T & { id: string };
  return { ...input, id: uuidv4() };
}
import type {
  UserPresence,
  ApplicationSettings,
  UserSettings,
  UserPoints,
  PollVote,
  PointsLedger,
  LeaderboardSettings,
  NotificationToken,
  UserDeviceKey,
  PromptStats,
  LeaderboardEntry,
  UserPointsAggregate,
  ModeratorProfile,
  UserHornPreference,
  Frame,
  Filter,
  FrameVersion,
  FilterVersion,
  AppPersona,
  UserModerationHistory,
  ContentHiddenFromUser,
  ScreenCaptureEvent,
  UserTrust,
  ModerationMediaAccessLog,
  LoadingScreen,
  PromptSuggestionAudit,
  ConversationArchive,
  StoryHighlight,
  HornRepost,
  PostShare,
  MuteList,
  MFABackupCode,
  CommentLike,
  SavedContent,
  PostHashtag,
  HornHashtag,
  FollowRequest,
  ContentScore,
  SuggestedFollow,
  TemporaryBannerMessage,
} from './cdkModelsTypes';
import * as ops from './graphql/cdk-operations';

import {
  getUserProfile,
  listUserProfiles,
  createUserProfile,
  updateUserProfile,
  deleteUserProfile,
  profileByHandle,
  getFollow,
  listFollows,
  createFollow,
  updateFollow,
  deleteFollow,
  getPrompt,
  listPrompts,
  createPrompt,
  updatePrompt,
  deletePrompt,
  promptByCreatorUserId,
  promptByFrameId,
  promptByFilterId,
  promptByVisibilityStatus,
  promptByPostedByPersonaId,
  promptBySource,
  getPost,
  listPosts,
  createPost,
  updatePost,
  deletePost,
  postsByCreator,
  postsByVisibility,
  getPostComment,
  listPostComments,
  createPostComment,
  updatePostComment,
  deletePostComment,
  commentsByPost,
  commentRepliesByPost,
  getPostReaction,
  listPostReactions,
  createPostReaction,
  updatePostReaction,
  deletePostReaction,
  reactionsByPost,
  getResponse,
  listResponses,
  createResponse,
  updateResponse,
  deleteResponse,
  responseByPromptId,
  responseByResponderUserId,
  responseByAppliedFrameId,
  responseByAppliedFilterId,
  responseByVisibilityStatus,
  getResponseView,
  listResponseViews,
  createResponseView,
  updateResponseView,
  deleteResponseView,
  listResponseViewsByPrompt,
  listResponseViewsByViewer,
  listResponseViewsByResponse,
  getReaction,
  listReactions,
  createReaction,
  updateReaction,
  deleteReaction,
  reactionByResponseId,
  reactionByUserId,
  getConversation,
  listConversations,
  createConversation,
  updateConversation,
  deleteConversation,
  listConversationsByType,
  conversationsByCreator,
  getConversationArchive,
  listConversationArchives,
  createConversationArchive,
  deleteConversationArchive,
  archivesByUser,
  getGroupMembership,
  listGroupMemberships,
  createGroupMembership,
  updateGroupMembership,
  deleteGroupMembership,
  membershipsByUser,
  membersByConversation,
  getSystemEvent,
  listSystemEvents,
  createSystemEvent,
  updateSystemEvent,
  deleteSystemEvent,
  eventsByConversation,
  getGroupEncryptionKey,
  listGroupEncryptionKeys,
  createGroupEncryptionKey,
  updateGroupEncryptionKey,
  deleteGroupEncryptionKey,
  keysByConversation,
  getGroupPrompt,
  listGroupPrompts,
  createGroupPrompt,
  updateGroupPrompt,
  deleteGroupPrompt,
  promptsByConversation,
  groupsByPrompt,
  getMessage,
  listMessages,
  createMessage,
  updateMessage,
  deleteMessage,
  messageByConversationId,
  legacyMessagesByExpiry,
  getEncryptedMessage,
  listEncryptedMessages,
  createEncryptedMessage,
  updateEncryptedMessage,
  deleteEncryptedMessage,
  encryptedMessageByConversationId,
  encryptedMessageByRecipientDeviceId,
  encryptedMessageByMessageGroupId,
  messagesByExpiry,
  getMessageReaction,
  listMessageReactions,
  createMessageReaction,
  updateMessageReaction,
  deleteMessageReaction,
  messageReactionByMessageId,
  messageReactionByMessageGroupId,
  messageReactionByConversationId,
  reactionsByExpiry,
  getUnsolicitedMessageTracker,
  listUnsolicitedMessageTrackers,
  createUnsolicitedMessageTracker,
  updateUnsolicitedMessageTracker,
  deleteUnsolicitedMessageTracker,
  unsolicitedMessageTrackerBySenderId,
  getBlockList,
  listBlockLists,
  createBlockList,
  updateBlockList,
  deleteBlockList,
  blockListByBlockerUserId,
  blockListByBlockedUserId,
  getMuteList,
  listMuteLists,
  createMuteList,
  updateMuteList,
  deleteMuteList,
  muteListByMuterUserId,
  muteListByMutedUserId,
  getReadReceipt,
  listReadReceipts,
  createReadReceipt,
  updateReadReceipt,
  deleteReadReceipt,
  readReceiptByConversationId,
  readReceiptByUserId,
  getTypingIndicator,
  listTypingIndicators,
  createTypingIndicator,
  updateTypingIndicator,
  deleteTypingIndicator,
  typingByConversation,
  getMessageMention,
  listMessageMentions,
  createMessageMention,
  updateMessageMention,
  deleteMessageMention,
  mentionsByUser,
  mentionsByConversation,
  getPinnedMessage,
  listPinnedMessages,
  createPinnedMessage,
  updatePinnedMessage,
  deletePinnedMessage,
  pinnedByConversation,
  getPoll,
  listPolls,
  createPoll,
  updatePoll,
  deletePoll,
  pollsByConversation,
  pollsByAudience,
  pollsByCreator,
  getFlag,
  listFlags,
  createFlag,
  updateFlag,
  deleteFlag,
  flagByContentId,
  flagByReporterId,
  flagByReporterContentKey,
  flagByStatus,
  getModerationCase,
  listModerationCases,
  createModerationCase,
  updateModerationCase,
  deleteModerationCase,
  moderationCaseByContentId,
  moderationCaseByCaseStatus,
  moderationCaseByAssignedTo,
  moderationCaseByContentOwnerId,
  getModerationAction,
  listModerationActions,
  createModerationAction,
  updateModerationAction,
  deleteModerationAction,
  moderationActionByCaseId,
  moderationActionByActorId,
  getStory,
  listStories,
  createStory,
  updateStory,
  deleteStory,
  listStoriesByCreator,
  listStoriesByExpiry,
  listStoriesByVisibility,
  getStorySegment,
  listStorySegments,
  createStorySegment,
  updateStorySegment,
  deleteStorySegment,
  listSegmentsByStory,
  listSegmentsByCreator,
  getStoryView,
  listStoryViews,
  createStoryView,
  updateStoryView,
  deleteStoryView,
  listViewsByStory,
  listViewsByViewer,
  listViewsBySegment,
  getStoryReaction,
  listStoryReactions,
  createStoryReaction,
  updateStoryReaction,
  deleteStoryReaction,
  listReactionsByStory,
  listReactionsBySegment,
  listReactionsByUser,
  listReactionsByStoryAndUser,
  getCloseFriend,
  listCloseFriends,
  createCloseFriend,
  updateCloseFriend,
  deleteCloseFriend,
  closeFriendsByUser,
  closeFriendsByFriend,
  getStoryMute,
  listStoryMutes,
  createStoryMute,
  updateStoryMute,
  deleteStoryMute,
  storyMutesByUser,
  getStoryTopRowFavorite,
  listStoryTopRowFavorites,
  createStoryTopRowFavorite,
  updateStoryTopRowFavorite,
  deleteStoryTopRowFavorite,
  topRowFavoritesByUser,
  topRowFavoritesByFavorite,
  getPromptSuggestion,
  listPromptSuggestions,
  createPromptSuggestion,
  updatePromptSuggestion,
  deletePromptSuggestion,
  suggestionsByCategory,
  suggestionsByVisibility,
  suggestionsByLanguage,
  suggestionsByModerationStatus,
  suggestionsByCreator,
  automationEligibleSuggestions,
  getAutomationRule,
  listAutomationRules,
  createAutomationRule,
  updateAutomationRule,
  deleteAutomationRule,
  automationRuleByPersonaId,
  automationRuleByEnabledStatus,
  automationRuleBySourceType,
  getAutomationExecution,
  listAutomationExecutions,
  createAutomationExecution,
  updateAutomationExecution,
  deleteAutomationExecution,
  executionsByRule,
  executionsByPersona,
  executionsByStatus,
  executionsByPrompt,
  getAgeVerification,
  listAgeVerifications,
  createAgeVerification,
  updateAgeVerification,
  deleteAgeVerification,
  ageVerificationsByStatus,
  ageVerificationsByDocHash,
  getVerificationAttempt,
  listVerificationAttempts,
  createVerificationAttempt,
  updateVerificationAttempt,
  deleteVerificationAttempt,
  attemptsByUser,
  attemptsByDocHash,
  getHorn,
  listHorns,
  createHorn,
  updateHorn,
  deleteHorn,
  listHornsByCreator,
  hornsByFeedExpiry,
  hornsByProfileExpiry,
  hornsByVisibility,
  getHornEngagement,
  listHornEngagements,
  createHornEngagement,
  updateHornEngagement,
  deleteHornEngagement,
  engagementsByHorn,
  engagementsByViewer,
  getHornComment,
  listHornComments,
  createHornComment,
  updateHornComment,
  deleteHornComment,
  commentsByHorn,
  commentRepliesByHorn,
  getHornRepost,
  listHornReposts,
  createHornRepost,
  updateHornRepost,
  deleteHornRepost,
  repostsByReposter,
  repostsOfHorn,
  repostsByOriginalCreator,
  getPostShare,
  listPostShares,
  createPostShare,
  updatePostShare,
  deletePostShare,
  sharesBySharer,
  sharesOfPost,
  sharesByOriginalCreator,
  getMFABackupCode,
  listMFABackupCodes,
  createMFABackupCode,
  updateMFABackupCode,
  deleteMFABackupCode,
  backupCodesByUserId,
  getFeedback,
  listFeedbacks,
  createFeedback,
  updateFeedback,
  deleteFeedback,
  getTesterSignup,
  listTesterSignups,
  createTesterSignup,
  updateTesterSignup,
  deleteTesterSignup,
  testerSignupByStatus,
  followByFollowerId,
  followByFollowingId,
  getCommentLike,
  listCommentLikes,
  createCommentLike,
  updateCommentLike,
  deleteCommentLike,
  likesByComment,
  likesByUser,
  getSavedContent,
  listSavedContents,
  createSavedContent,
  updateSavedContent,
  deleteSavedContent,
  savedContentByUser,
  getPostHashtag,
  listPostHashtags,
  createPostHashtag,
  updatePostHashtag,
  deletePostHashtag,
  postsByHashtag,
  getHornHashtag,
  listHornHashtags,
  createHornHashtag,
  updateHornHashtag,
  deleteHornHashtag,
  hornsByHashtag,
  getFollowRequest,
  listFollowRequests,
  createFollowRequest,
  updateFollowRequest,
  deleteFollowRequest,
  requestsByTarget,
  requestsByRequester,
  getContentScore,
  listContentScores,
  createContentScore,
  updateContentScore,
  deleteContentScore,
  scoresByContentType,
  getSuggestedFollow,
  listSuggestedFollows,
  createSuggestedFollow,
  updateSuggestedFollow,
  deleteSuggestedFollow,
  suggestionsByTargetUser,
  getTemporaryBannerMessage,
  listTemporaryBannerMessages,
  createTemporaryBannerMessage,
  updateTemporaryBannerMessage,
  deleteTemporaryBannerMessage,
} from './graphql/cdk-operations';

import type {
  UserProfile,
  Follow,
  Prompt,
  Post,
  PostComment,
  PostReaction,
  Response,
  ResponseView,
  Reaction,
  Conversation,
  GroupMembership,
  SystemEvent,
  GroupEncryptionKey,
  GroupPrompt,
  Message,
  EncryptedMessage,
  MessageReaction,
  UnsolicitedMessageTracker,
  BlockList,
  ReadReceipt,
  TypingIndicator,
  MessageMention,
  PinnedMessage,
  Poll,
  Flag,
  ModerationCase,
  ModerationAction,
  Story,
  StorySegment,
  StoryView,
  StoryReaction,
  CloseFriend,
  StoryMute,
  StoryTopRowFavorite,
  PromptSuggestion,
  AutomationRule,
  AutomationExecution,
  AgeVerification,
  VerificationAttempt,
  Horn,
  HornEngagement,
  HornComment,
  Feedback,
  TesterSignup,
} from './cdkModelsTypes';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

let _client: any = null;

function getClient() {
  if (!_client) {
    const raw = generateClient();
    // Wrap the raw client so every .graphql() call uses Cognito User Pool auth.
    // The API default may be IAM (for Lambda access), but authenticated user
    // operations should always use the User Pool token.
    _client = {
      graphql: (options: any) =>
        raw.graphql({ ...options, authMode: 'userPool' }),
    };
  }
  return _client;
}

/** Reset the lazy-initialised client (e.g. after Amplify.configure). */
export function resetCdkClient() {
  _client = null;
}

async function gqlOp<T>(
  query: string,
  variables: any,
): Promise<{ data: T | null; errors: any[] | null }> {
  try {
    const result = (await getClient().graphql({ query, variables })) as any;
    // Check for errors in the result (GraphQL can return partial results with errors)
    if (result.errors && result.errors.length > 0) {
      console.error('GraphQL errors in result:', result.errors);
      return { data: null, errors: result.errors };
    }
    const key = Object.keys(result.data)[0];
    return { data: result.data[key] as T, errors: null };
  } catch (err: any) {
    console.error('GraphQL operation threw:', err);
    return { data: null, errors: err.errors || [err] };
  }
}

async function gqlList<T>(
  query: string,
  variables: any,
): Promise<{ data: T[]; nextToken?: string; errors: any[] | null }> {
  try {
    // Serialize filter to JSON string — schema defines filter as AWSJSON scalar
    const vars = variables?.filter
      ? { ...variables, filter: typeof variables.filter === 'string' ? variables.filter : JSON.stringify(variables.filter) }
      : variables;
    const result = (await getClient().graphql({ query, variables: vars })) as any;
    // Check for errors in the result (GraphQL can return partial results with errors)
    if (result.errors && result.errors.length > 0) {
      console.error('GraphQL list errors in result:', result.errors);
      return { data: [], errors: result.errors };
    }
    const key = Object.keys(result.data)[0];
    return {
      data: result.data[key].items as T[],
      nextToken: result.data[key].nextToken,
      errors: null,
    };
  } catch (err: any) {
    return { data: [], errors: err.errors || [err] };
  }
}

// ---------------------------------------------------------------------------
// Public client
// ---------------------------------------------------------------------------

export const cdkDataClient = {
  models: {
    // ========== UserPresence ==========
    UserPresence: {
      get: (keys: Pick<UserPresence, 'userId'>) =>
        gqlOp<UserPresence>(ops.getUserPresence, keys),
      create: (input: Partial<UserPresence> & Pick<UserPresence, 'userId' | 'lastSeenAt'>) =>
        gqlOp<UserPresence>(ops.createUserPresence, { input }),
      update: (input: Partial<UserPresence> & Pick<UserPresence, 'userId'>) =>
        gqlOp<UserPresence>(ops.updateUserPresence, { input }),
      delete: (keys: Pick<UserPresence, 'userId'>) =>
        gqlOp<UserPresence>(ops.deleteUserPresence, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserPresence>(ops.listUserPresences, opts || {}),
    },

    // ========== ApplicationSettings ==========
    ApplicationSettings: {
      get: (keys: Pick<ApplicationSettings, 'key'>) =>
        gqlOp<ApplicationSettings>(ops.getApplicationSettings, keys),
      create: (input: Partial<ApplicationSettings> & Pick<ApplicationSettings, 'key' | 'value'>) =>
        gqlOp<ApplicationSettings>(ops.createApplicationSettings, { input }),
      update: (input: Partial<ApplicationSettings> & Pick<ApplicationSettings, 'key'>) =>
        gqlOp<ApplicationSettings>(ops.updateApplicationSettings, { input }),
      delete: (keys: Pick<ApplicationSettings, 'key'>) =>
        gqlOp<ApplicationSettings>(ops.deleteApplicationSettings, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<ApplicationSettings>(ops.listApplicationSettings, opts || {}),
    },

    // ========== UserSettings ==========
    UserSettings: {
      get: (keys: Pick<UserSettings, 'id'>) =>
        gqlOp<UserSettings>(ops.getUserSettings, keys),
      create: (input: Partial<UserSettings> & { id?: string }) =>
        gqlOp<UserSettings>(ops.createUserSettings, { input }),
      update: (input: Partial<UserSettings> & Pick<UserSettings, 'id'>) =>
        gqlOp<UserSettings>(ops.updateUserSettings, { input }),
      delete: (keys: Pick<UserSettings, 'id'>) =>
        gqlOp<UserSettings>(ops.deleteUserSettings, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserSettings>(ops.listUserSettings, opts || {}),
    },

    // ========== UserPoints ==========
    UserPoints: {
      get: (keys: Pick<UserPoints, 'id'>) =>
        gqlOp<UserPoints>(ops.getUserPoints, keys),
      create: (input: Partial<UserPoints> & { id?: string }) =>
        gqlOp<UserPoints>(ops.createUserPoints, { input }),
      update: (input: Partial<UserPoints> & Pick<UserPoints, 'id'>) =>
        gqlOp<UserPoints>(ops.updateUserPoints, { input }),
      delete: (keys: Pick<UserPoints, 'id'>) =>
        gqlOp<UserPoints>(ops.deleteUserPoints, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserPoints>(ops.listUserPoints, opts || {}),
    },

    // ========== PollVote ==========
    PollVote: {
      get: (keys: Pick<PollVote, 'pollId' | 'voterId'>) =>
        gqlOp<PollVote>(ops.getPollVote, keys),
      create: (input: Partial<PollVote> & Pick<PollVote, 'pollId' | 'voterId' | 'optionIndices'>) =>
        gqlOp<PollVote>(ops.createPollVote, { input }),
      update: (input: Partial<PollVote> & Pick<PollVote, 'pollId' | 'voterId'>) =>
        gqlOp<PollVote>(ops.updatePollVote, { input }),
      delete: (keys: Pick<PollVote, 'pollId' | 'voterId'>) =>
        gqlOp<PollVote>(ops.deletePollVote, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<PollVote>(ops.listPollVotes, opts || {}),
      votesByPoll: (opts: { pollId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<PollVote>(ops.votesByPoll, opts),
    },

    // ========== PointsLedger ==========
    PointsLedger: {
      get: (keys: Pick<PointsLedger, 'id'>) =>
        gqlOp<PointsLedger>(ops.getPointsLedger, keys),
      create: (input: Partial<PointsLedger> & { id?: string } & Pick<PointsLedger, 'userId' | 'pointsDelta'>) =>
        gqlOp<PointsLedger>(ops.createPointsLedger, { input: ensureId(input) }),
      update: (input: Partial<PointsLedger> & Pick<PointsLedger, 'id'>) =>
        gqlOp<PointsLedger>(ops.updatePointsLedger, { input }),
      delete: (keys: Pick<PointsLedger, 'id'>) =>
        gqlOp<PointsLedger>(ops.deletePointsLedger, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<PointsLedger>(ops.listPointsLedgers, opts || {}),
      ledgerByUserId: (opts: { userId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<PointsLedger>(ops.ledgerByUserId, opts),
    },

    // ========== LeaderboardSettings ==========
    LeaderboardSettings: {
      get: (keys: Pick<LeaderboardSettings, 'id'>) =>
        gqlOp<LeaderboardSettings>(ops.getLeaderboardSettings, keys),
      create: (input: Partial<LeaderboardSettings> & { id?: string }) =>
        gqlOp<LeaderboardSettings>(ops.createLeaderboardSettings, { input }),
      update: (input: Partial<LeaderboardSettings> & Pick<LeaderboardSettings, 'id'>) =>
        gqlOp<LeaderboardSettings>(ops.updateLeaderboardSettings, { input }),
      delete: (keys: Pick<LeaderboardSettings, 'id'>) =>
        gqlOp<LeaderboardSettings>(ops.deleteLeaderboardSettings, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<LeaderboardSettings>(ops.listLeaderboardSettings, opts || {}),
    },

    // ========== NotificationToken ==========
    NotificationToken: {
      get: (keys: Pick<NotificationToken, 'userId' | 'deviceId'>) =>
        gqlOp<NotificationToken>(ops.getNotificationToken, keys),
      create: (input: Partial<NotificationToken> & Pick<NotificationToken, 'userId' | 'deviceId' | 'token'>) =>
        gqlOp<NotificationToken>(ops.createNotificationToken, { input }),
      update: (input: Partial<NotificationToken> & Pick<NotificationToken, 'userId' | 'deviceId'>) =>
        gqlOp<NotificationToken>(ops.updateNotificationToken, { input }),
      delete: (keys: Pick<NotificationToken, 'userId' | 'deviceId'>) =>
        gqlOp<NotificationToken>(ops.deleteNotificationToken, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<NotificationToken>(ops.listNotificationTokens, opts || {}),
      tokensByUserId: (opts: { userId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<NotificationToken>(ops.tokensByUserId, opts),
    },

    // ========== UserDeviceKey ==========
    UserDeviceKey: {
      get: (keys: Pick<UserDeviceKey, 'userId' | 'deviceId'>) =>
        gqlOp<UserDeviceKey>(ops.getUserDeviceKey, keys),
      create: (input: Partial<UserDeviceKey> & Pick<UserDeviceKey, 'userId' | 'deviceId' | 'publicIdentityKey' | 'publicPreKey' | 'publicPreKeySignature'>) =>
        gqlOp<UserDeviceKey>(ops.createUserDeviceKey, { input }),
      update: (input: Partial<UserDeviceKey> & Pick<UserDeviceKey, 'userId' | 'deviceId'>) =>
        gqlOp<UserDeviceKey>(ops.updateUserDeviceKey, { input }),
      delete: (keys: Pick<UserDeviceKey, 'userId' | 'deviceId'>) =>
        gqlOp<UserDeviceKey>(ops.deleteUserDeviceKey, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserDeviceKey>(ops.listUserDeviceKeys, opts || {}),
      deviceKeysByUser: (opts: { userId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<UserDeviceKey>(ops.deviceKeysByUser, { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined }),
    },

    // ========== PromptStats ==========
    PromptStats: {
      get: (keys: Pick<PromptStats, 'id'>) =>
        gqlOp<PromptStats>(ops.getPromptStats, keys),
      create: (input: Partial<PromptStats> & { id?: string } & Pick<PromptStats, 'promptId' | 'creatorUserId'>) =>
        gqlOp<PromptStats>(ops.createPromptStats, { input }),
      update: (input: Partial<PromptStats> & Pick<PromptStats, 'id'>) =>
        gqlOp<PromptStats>(ops.updatePromptStats, { input }),
      delete: (keys: Pick<PromptStats, 'id'>) =>
        gqlOp<PromptStats>(ops.deletePromptStats, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<PromptStats>(ops.listPromptStats, opts || {}),
      statsByPromptId: (opts: { promptId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<PromptStats>(ops.statsByPromptId, opts),
    },

    // ========== LeaderboardEntry ==========
    LeaderboardEntry: {
      get: (keys: Pick<LeaderboardEntry, 'id'>) =>
        gqlOp<LeaderboardEntry>(ops.getLeaderboardEntry, keys),
      create: (input: Partial<LeaderboardEntry> & { id?: string } & Pick<LeaderboardEntry, 'userId' | 'rank' | 'userHandle' | 'userDisplayName' | 'computedAt' | 'windowRankKey'>) =>
        gqlOp<LeaderboardEntry>(ops.createLeaderboardEntry, { input }),
      update: (input: Partial<LeaderboardEntry> & Pick<LeaderboardEntry, 'id'>) =>
        gqlOp<LeaderboardEntry>(ops.updateLeaderboardEntry, { input }),
      delete: (keys: Pick<LeaderboardEntry, 'id'>) =>
        gqlOp<LeaderboardEntry>(ops.deleteLeaderboardEntry, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<LeaderboardEntry>(ops.listLeaderboardEntries, opts || {}),
      // GSI query
      leaderboardByWindow: (opts: { timeWindow: string; rank?: any; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<LeaderboardEntry>(ops.leaderboardByWindow, opts),
    },

    // ========== UserPointsAggregate ==========
    UserPointsAggregate: {
      get: (keys: Pick<UserPointsAggregate, 'userId'>) =>
        gqlOp<UserPointsAggregate>(ops.getUserPointsAggregate, keys),
      create: (input: Partial<UserPointsAggregate> & Pick<UserPointsAggregate, 'userId' | 'lastUpdatedAt'>) =>
        gqlOp<UserPointsAggregate>(ops.createUserPointsAggregate, { input }),
      update: (input: Partial<UserPointsAggregate> & Pick<UserPointsAggregate, 'userId'>) =>
        gqlOp<UserPointsAggregate>(ops.updateUserPointsAggregate, { input }),
      delete: (keys: Pick<UserPointsAggregate, 'userId'>) =>
        gqlOp<UserPointsAggregate>(ops.deleteUserPointsAggregate, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserPointsAggregate>(ops.listUserPointsAggregates, opts || {}),
    },

    // ========== ModeratorProfile ==========
    ModeratorProfile: {
      get: (keys: Pick<ModeratorProfile, 'id'>) =>
        gqlOp<ModeratorProfile>(ops.getModeratorProfile, keys),
      create: (input: Partial<ModeratorProfile> & { id?: string } & Pick<ModeratorProfile, 'userId'>) =>
        gqlOp<ModeratorProfile>(ops.createModeratorProfile, { input: ensureId(input) }),
      update: (input: Partial<ModeratorProfile> & Pick<ModeratorProfile, 'id'>) =>
        gqlOp<ModeratorProfile>(ops.updateModeratorProfile, { input }),
      delete: (keys: Pick<ModeratorProfile, 'id'>) =>
        gqlOp<ModeratorProfile>(ops.deleteModeratorProfile, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<ModeratorProfile>(ops.listModeratorProfiles, opts || {}),
    },

    // ========== UserHornPreference ==========
    UserHornPreference: {
      get: (keys: Pick<UserHornPreference, 'userId'>) =>
        gqlOp<UserHornPreference>(ops.getUserHornPreference, keys),
      create: (input: Partial<UserHornPreference> & Pick<UserHornPreference, 'userId'>) =>
        gqlOp<UserHornPreference>(ops.createUserHornPreference, { input }),
      update: (input: Partial<UserHornPreference> & Pick<UserHornPreference, 'userId'>) =>
        gqlOp<UserHornPreference>(ops.updateUserHornPreference, { input }),
      delete: (keys: Pick<UserHornPreference, 'userId'>) =>
        gqlOp<UserHornPreference>(ops.deleteUserHornPreference, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserHornPreference>(ops.listUserHornPreferences, opts || {}),
    },

    // ========== Frame ==========
    Frame: {
      get: (keys: Pick<Frame, 'id'>) =>
        gqlOp<Frame>(ops.getFrame, keys),
      create: (input: Partial<Frame> & { id?: string } & Pick<Frame, 'name' | 'currentVersionId' | 'currentVersionHash'>) =>
        gqlOp<Frame>(ops.createFrame, { input: ensureId(input) }),
      update: (input: Partial<Frame> & Pick<Frame, 'id'>) =>
        gqlOp<Frame>(ops.updateFrame, { input }),
      delete: (keys: Pick<Frame, 'id'>) =>
        gqlOp<Frame>(ops.deleteFrame, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<Frame>(ops.listFrames, opts || {}),
      framesByVisibility: (opts: { visibilityStatus: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<Frame>(ops.framesByVisibility, { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined }),
    },

    // ========== Filter ==========
    Filter: {
      get: (keys: Pick<Filter, 'id'>) =>
        gqlOp<Filter>(ops.getFilter, keys),
      create: (input: Partial<Filter> & { id?: string } & Pick<Filter, 'name' | 'currentVersionId' | 'currentVersionHash'>) =>
        gqlOp<Filter>(ops.createFilter, { input: ensureId(input) }),
      update: (input: Partial<Filter> & Pick<Filter, 'id'>) =>
        gqlOp<Filter>(ops.updateFilter, { input }),
      delete: (keys: Pick<Filter, 'id'>) =>
        gqlOp<Filter>(ops.deleteFilter, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<Filter>(ops.listFilters, opts || {}),
      filtersByVisibility: (opts: { visibilityStatus: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<Filter>(ops.filtersByVisibility, { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined }),
    },

    // ========== FrameVersion ==========
    FrameVersion: {
      get: (keys: Pick<FrameVersion, 'id'>) =>
        gqlOp<FrameVersion>(ops.getFrameVersion, keys),
      create: (input: Partial<FrameVersion> & { id?: string } & Pick<FrameVersion, 'frameId' | 'versionNumber' | 'contentHash' | 'overlayImagePath' | 'thumbnailPath' | 'aspectRatio' | 'safeAreaInsets' | 'imageWidth' | 'imageHeight' | 'fileSizeBytes' | 'uploadedBy' | 'uploadedAt'>) =>
        gqlOp<FrameVersion>(ops.createFrameVersion, { input: ensureId(input) }),
      update: (input: Partial<FrameVersion> & Pick<FrameVersion, 'id'>) =>
        gqlOp<FrameVersion>(ops.updateFrameVersion, { input }),
      delete: (keys: Pick<FrameVersion, 'id'>) =>
        gqlOp<FrameVersion>(ops.deleteFrameVersion, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<FrameVersion>(ops.listFrameVersions, opts || {}),
      versionsByFrame: (opts: { frameId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<FrameVersion>(ops.versionsByFrame, { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined }),
    },

    // ========== FilterVersion ==========
    FilterVersion: {
      get: (keys: Pick<FilterVersion, 'id'>) =>
        gqlOp<FilterVersion>(ops.getFilterVersion, keys),
      create: (input: Partial<FilterVersion> & { id?: string } & Pick<FilterVersion, 'filterId' | 'versionNumber' | 'contentHash' | 'filterAssetPath' | 'thumbnailPath' | 'fileSizeBytes' | 'uploadedBy' | 'uploadedAt'>) =>
        gqlOp<FilterVersion>(ops.createFilterVersion, { input: ensureId(input) }),
      update: (input: Partial<FilterVersion> & Pick<FilterVersion, 'id'>) =>
        gqlOp<FilterVersion>(ops.updateFilterVersion, { input }),
      delete: (keys: Pick<FilterVersion, 'id'>) =>
        gqlOp<FilterVersion>(ops.deleteFilterVersion, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<FilterVersion>(ops.listFilterVersions, opts || {}),
      versionsByFilter: (opts: { filterId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<FilterVersion>(ops.versionsByFilter, { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined }),
    },

    // ========== AppPersona ==========
    AppPersona: {
      get: (keys: Pick<AppPersona, 'id'>) =>
        gqlOp<AppPersona>(ops.getAppPersona, keys),
      create: (input: Partial<AppPersona> & { id?: string } & Pick<AppPersona, 'displayName' | 'handle' | 'avatarUrl' | 'createdBy'>) =>
        gqlOp<AppPersona>(ops.createAppPersona, { input: ensureId(input) }),
      update: (input: Partial<AppPersona> & Pick<AppPersona, 'id'>) =>
        gqlOp<AppPersona>(ops.updateAppPersona, { input }),
      delete: (keys: Pick<AppPersona, 'id'>) =>
        gqlOp<AppPersona>(ops.deleteAppPersona, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<AppPersona>(ops.listAppPersonas, opts || {}),
    },

    // ========== UserModerationHistory ==========
    UserModerationHistory: {
      get: (keys: Pick<UserModerationHistory, 'id'>) =>
        gqlOp<UserModerationHistory>(ops.getUserModerationHistory, keys),
      create: (input: Partial<UserModerationHistory> & { id?: string } & Pick<UserModerationHistory, 'userId' | 'reason' | 'issuedBy'>) =>
        gqlOp<UserModerationHistory>(ops.createUserModerationHistory, { input: ensureId(input) }),
      update: (input: Partial<UserModerationHistory> & Pick<UserModerationHistory, 'id'>) =>
        gqlOp<UserModerationHistory>(ops.updateUserModerationHistory, { input }),
      delete: (keys: Pick<UserModerationHistory, 'id'>) =>
        gqlOp<UserModerationHistory>(ops.deleteUserModerationHistory, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserModerationHistory>(ops.listUserModerationHistories, opts || {}),
    },

    // ========== ContentHiddenFromUser ==========
    ContentHiddenFromUser: {
      get: (keys: Pick<ContentHiddenFromUser, 'id'>) =>
        gqlOp<ContentHiddenFromUser>(ops.getContentHiddenFromUser, keys),
      create: (input: Partial<ContentHiddenFromUser> & { id?: string } & Pick<ContentHiddenFromUser, 'userId' | 'contentId' | 'userContentKey'>) =>
        gqlOp<ContentHiddenFromUser>(ops.createContentHiddenFromUser, { input: ensureId(input) }),
      update: (input: Partial<ContentHiddenFromUser> & Pick<ContentHiddenFromUser, 'id'>) =>
        gqlOp<ContentHiddenFromUser>(ops.updateContentHiddenFromUser, { input }),
      delete: (keys: Pick<ContentHiddenFromUser, 'id'>) =>
        gqlOp<ContentHiddenFromUser>(ops.deleteContentHiddenFromUser, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<ContentHiddenFromUser>(ops.listContentHiddenFromUsers, opts || {}),
      hiddenContentByUser: (opts: { userId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<ContentHiddenFromUser>(ops.hiddenContentByUser, { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined }),
    },

    // ========== ScreenCaptureEvent ==========
    ScreenCaptureEvent: {
      get: (keys: Pick<ScreenCaptureEvent, 'id'>) =>
        gqlOp<ScreenCaptureEvent>(ops.getScreenCaptureEvent, keys),
      create: (input: Partial<ScreenCaptureEvent> & { id?: string } & Pick<ScreenCaptureEvent, 'contentId' | 'ownerUserId' | 'viewerUserId' | 'timestamp'>) =>
        gqlOp<ScreenCaptureEvent>(ops.createScreenCaptureEvent, { input: ensureId(input) }),
      update: (input: Partial<ScreenCaptureEvent> & Pick<ScreenCaptureEvent, 'id'>) =>
        gqlOp<ScreenCaptureEvent>(ops.updateScreenCaptureEvent, { input }),
      delete: (keys: Pick<ScreenCaptureEvent, 'id'>) =>
        gqlOp<ScreenCaptureEvent>(ops.deleteScreenCaptureEvent, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<ScreenCaptureEvent>(ops.listScreenCaptureEvents, opts || {}),
      // GSI query
      listScreenCaptureEventByContentId: (opts: { contentId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<ScreenCaptureEvent>(ops.listScreenCaptureEventByContentId, opts),
    },

    // ========== UserTrust ==========
    UserTrust: {
      get: (keys: Pick<UserTrust, 'id'>) =>
        gqlOp<UserTrust>(ops.getUserTrust, keys),
      create: (input: Partial<UserTrust> & { id?: string } & Pick<UserTrust, 'userId'>) =>
        gqlOp<UserTrust>(ops.createUserTrust, { input: ensureId(input) }),
      update: (input: Partial<UserTrust> & Pick<UserTrust, 'id'>) =>
        gqlOp<UserTrust>(ops.updateUserTrust, { input }),
      delete: (keys: Pick<UserTrust, 'id'>) =>
        gqlOp<UserTrust>(ops.deleteUserTrust, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<UserTrust>(ops.listUserTrusts, opts || {}),
      trustByUserId: (opts: { userId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<UserTrust>(ops.trustByUserId, { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined }),
    },

    // ========== ModerationMediaAccessLog ==========
    ModerationMediaAccessLog: {
      get: (keys: Pick<ModerationMediaAccessLog, 'id'>) =>
        gqlOp<ModerationMediaAccessLog>(ops.getModerationMediaAccessLog, keys),
      create: (input: Partial<ModerationMediaAccessLog> & { id?: string } & Pick<ModerationMediaAccessLog, 'moderatorUserId' | 'contentId' | 'mediaKey'>) =>
        gqlOp<ModerationMediaAccessLog>(ops.createModerationMediaAccessLog, { input: ensureId(input) }),
      update: (input: Partial<ModerationMediaAccessLog> & Pick<ModerationMediaAccessLog, 'id'>) =>
        gqlOp<ModerationMediaAccessLog>(ops.updateModerationMediaAccessLog, { input }),
      delete: (keys: Pick<ModerationMediaAccessLog, 'id'>) =>
        gqlOp<ModerationMediaAccessLog>(ops.deleteModerationMediaAccessLog, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<ModerationMediaAccessLog>(ops.listModerationMediaAccessLogs, opts || {}),
    },

    // ========== LoadingScreen ==========
    LoadingScreen: {
      get: (keys: Pick<LoadingScreen, 'id'>) =>
        gqlOp<LoadingScreen>(ops.getLoadingScreen, keys),
      create: (input: Partial<LoadingScreen> & { id?: string } & Pick<LoadingScreen, 'imageS3Key'>) =>
        gqlOp<LoadingScreen>(ops.createLoadingScreen, { input: ensureId(input) }),
      update: (input: Partial<LoadingScreen> & Pick<LoadingScreen, 'id'>) =>
        gqlOp<LoadingScreen>(ops.updateLoadingScreen, { input }),
      delete: (keys: Pick<LoadingScreen, 'id'>) =>
        gqlOp<LoadingScreen>(ops.deleteLoadingScreen, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<LoadingScreen>(ops.listLoadingScreens, opts || {}),
      // GSI query
      activeLoadingScreens: (opts: { isActive: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<LoadingScreen>(ops.activeLoadingScreens, opts),
    },

    // ========== PromptSuggestionAudit ==========
    PromptSuggestionAudit: {
      get: (keys: Pick<PromptSuggestionAudit, 'id'>) =>
        gqlOp<PromptSuggestionAudit>(ops.getPromptSuggestionAudit, keys),
      create: (input: Partial<PromptSuggestionAudit> & { id?: string } & Pick<PromptSuggestionAudit, 'suggestionId' | 'actorId' | 'timestamp'>) =>
        gqlOp<PromptSuggestionAudit>(ops.createPromptSuggestionAudit, { input: ensureId(input) }),
      update: (input: Partial<PromptSuggestionAudit> & Pick<PromptSuggestionAudit, 'id'>) =>
        gqlOp<PromptSuggestionAudit>(ops.updatePromptSuggestionAudit, { input }),
      delete: (keys: Pick<PromptSuggestionAudit, 'id'>) =>
        gqlOp<PromptSuggestionAudit>(ops.deletePromptSuggestionAudit, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<PromptSuggestionAudit>(ops.listPromptSuggestionAudits, opts || {}),
      // GSI query
      auditBySuggestion: (opts: { suggestionId: string; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) =>
        gqlList<PromptSuggestionAudit>(ops.auditBySuggestion, opts),
    },
    UserProfile: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getUserProfile, variables: { id: id } });
        return { data: (result as any).data?.getUserProfile ?? null as UserProfile | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listUserProfiles, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listUserProfiles;
        return { data: (conn?.items ?? []) as UserProfile[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createUserProfile, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createUserProfile ?? null as UserProfile | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateUserProfile, variables: { input: input } });
        return { data: (result as any).data?.updateUserProfile ?? null as UserProfile | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteUserProfile, variables: { id: id } });
        return { data: (result as any).data?.deleteUserProfile ?? null as UserProfile | null, errors: null as any };
      },
      profileByHandle: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: profileByHandle, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.profileByHandle;
        return { data: (conn?.items ?? []) as UserProfile[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      get listProfileByHandle() { return (this as any).profileByHandle; },
    },
    Follow: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getFollow, variables: { id: id } });
        return { data: (result as any).data?.getFollow ?? null as Follow | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listFollows, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listFollows;
        return { data: (conn?.items ?? []) as Follow[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createFollow, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createFollow ?? null as Follow | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateFollow, variables: { input: input } });
        return { data: (result as any).data?.updateFollow ?? null as Follow | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteFollow, variables: { id: id } });
        return { data: (result as any).data?.deleteFollow ?? null as Follow | null, errors: null as any };
      },
          followByFollowerId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: followByFollowerId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.followByFollowerId;
        return { data: (conn?.items ?? []) as Follow[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      get listFollowByFollowerId() { return (this as any).followByFollowerId; },
          followByFollowingId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: followByFollowingId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.followByFollowingId;
        return { data: (conn?.items ?? []) as Follow[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      get listFollowByFollowingId() { return (this as any).followByFollowingId; },
    },
    Prompt: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getPrompt, variables: { id: id } });
        return { data: (result as any).data?.getPrompt ?? null as Prompt | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPrompts, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPrompts;
        return { data: (conn?.items ?? []) as Prompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPrompt, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createPrompt ?? null as Prompt | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePrompt, variables: { input: input } });
        return { data: (result as any).data?.updatePrompt ?? null as Prompt | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deletePrompt, variables: { id: id } });
        return { data: (result as any).data?.deletePrompt ?? null as Prompt | null, errors: null as any };
      },
      promptByCreatorUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: promptByCreatorUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.promptByCreatorUserId;
        return { data: (conn?.items ?? []) as Prompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      promptByFrameId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: promptByFrameId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.promptByFrameId;
        return { data: (conn?.items ?? []) as Prompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      promptByFilterId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: promptByFilterId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.promptByFilterId;
        return { data: (conn?.items ?? []) as Prompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      promptByVisibilityStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: promptByVisibilityStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.promptByVisibilityStatus;
        return { data: (conn?.items ?? []) as Prompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      promptByPostedByPersonaId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: promptByPostedByPersonaId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.promptByPostedByPersonaId;
        return { data: (conn?.items ?? []) as Prompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      promptBySource: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: promptBySource, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.promptBySource;
        return { data: (conn?.items ?? []) as Prompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listPromptByCreatorUserId() { return (this as any).promptByCreatorUserId; },
      get listPromptByFrameId() { return (this as any).promptByFrameId; },
      get listPromptByFilterId() { return (this as any).promptByFilterId; },
      get listPromptByVisibilityStatus() { return (this as any).promptByVisibilityStatus; },
      get listPromptByPostedByPersonaId() { return (this as any).promptByPostedByPersonaId; },
      get listPromptBySource() { return (this as any).promptBySource; },
    },
    Post: {
      get: async (keys: { id: string; publishedAt?: string }) => {
        const result = await getClient().graphql({ query: getPost, variables: keys });
        return { data: (result as any).data?.getPost ?? null as Post | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPosts, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPosts;
        return { data: (conn?.items ?? []) as Post[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPost, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createPost ?? null as Post | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePost, variables: { input: input } });
        return { data: (result as any).data?.updatePost ?? null as Post | null, errors: null as any };
      },
      delete: async (keys: { id: string; publishedAt?: string }) => {
        const result = await getClient().graphql({ query: deletePost, variables: keys });
        return { data: (result as any).data?.deletePost ?? null as Post | null, errors: null as any };
      },
      postsByCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: postsByCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.postsByCreator;
        return { data: (conn?.items ?? []) as Post[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      postsByVisibility: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: postsByVisibility, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.postsByVisibility;
        return { data: (conn?.items ?? []) as Post[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listPostsByCreator() { return (this as any).postsByCreator; },
      get listPostsByVisibility() { return (this as any).postsByVisibility; },
    },
    PostComment: {
      get: async (keys: { id: string; commentedAt?: string }) => {
        const result = await getClient().graphql({ query: getPostComment, variables: keys });
        return { data: (result as any).data?.getPostComment ?? null as PostComment | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPostComments, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPostComments;
        return { data: (conn?.items ?? []) as PostComment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPostComment, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createPostComment ?? null as PostComment | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePostComment, variables: { input: input } });
        return { data: (result as any).data?.updatePostComment ?? null as PostComment | null, errors: null as any };
      },
      delete: async (keys: { id: string; commentedAt?: string }) => {
        const result = await getClient().graphql({ query: deletePostComment, variables: keys });
        return { data: (result as any).data?.deletePostComment ?? null as PostComment | null, errors: null as any };
      },
      commentsByPost: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: commentsByPost, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.commentsByPost;
        return { data: (conn?.items ?? []) as PostComment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      commentRepliesByPost: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: commentRepliesByPost, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.commentRepliesByPost;
        return { data: (conn?.items ?? []) as PostComment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },

      get listCommentsByPost() { return (this as any).commentsByPost; },
    },
    PostReaction: {
      get: async (keys: { id: string; reactedAt?: string }) => {
        const result = await getClient().graphql({ query: getPostReaction, variables: keys });
        return { data: (result as any).data?.getPostReaction ?? null as PostReaction | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPostReactions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPostReactions;
        return { data: (conn?.items ?? []) as PostReaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPostReaction, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createPostReaction ?? null as PostReaction | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePostReaction, variables: { input: input } });
        return { data: (result as any).data?.updatePostReaction ?? null as PostReaction | null, errors: null as any };
      },
      delete: async (keys: { id: string; reactedAt?: string }) => {
        const result = await getClient().graphql({ query: deletePostReaction, variables: keys });
        return { data: (result as any).data?.deletePostReaction ?? null as PostReaction | null, errors: null as any };
      },
      reactionsByPost: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: reactionsByPost, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.reactionsByPost;
        return { data: (conn?.items ?? []) as PostReaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listReactionsByPost() { return (this as any).reactionsByPost; },
    },
    Response: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getResponse, variables: { id: id } });
        return { data: (result as any).data?.getResponse ?? null as Response | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listResponses, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listResponses;
        return { data: (conn?.items ?? []) as Response[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createResponse, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createResponse ?? null as Response | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateResponse, variables: { input: input } });
        return { data: (result as any).data?.updateResponse ?? null as Response | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteResponse, variables: { id: id } });
        return { data: (result as any).data?.deleteResponse ?? null as Response | null, errors: null as any };
      },
      responseByPromptId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: responseByPromptId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.responseByPromptId;
        return { data: (conn?.items ?? []) as Response[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      responseByResponderUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: responseByResponderUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.responseByResponderUserId;
        return { data: (conn?.items ?? []) as Response[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      responseByAppliedFrameId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: responseByAppliedFrameId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.responseByAppliedFrameId;
        return { data: (conn?.items ?? []) as Response[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      responseByAppliedFilterId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: responseByAppliedFilterId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.responseByAppliedFilterId;
        return { data: (conn?.items ?? []) as Response[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      responseByVisibilityStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: responseByVisibilityStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.responseByVisibilityStatus;
        return { data: (conn?.items ?? []) as Response[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listResponseByPromptId() { return (this as any).responseByPromptId; },
      get listResponseByResponderUserId() { return (this as any).responseByResponderUserId; },
      get listResponseByAppliedFrameId() { return (this as any).responseByAppliedFrameId; },
      get listResponseByAppliedFilterId() { return (this as any).responseByAppliedFilterId; },
      get listResponseByVisibilityStatus() { return (this as any).responseByVisibilityStatus; },
    },
    ResponseView: {
      get: async (keys: { id: string; viewedAt?: string }) => {
        const result = await getClient().graphql({ query: getResponseView, variables: keys });
        return { data: (result as any).data?.getResponseView ?? null as ResponseView | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listResponseViews, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listResponseViews;
        return { data: (conn?.items ?? []) as ResponseView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createResponseView, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createResponseView ?? null as ResponseView | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateResponseView, variables: { input: input } });
        return { data: (result as any).data?.updateResponseView ?? null as ResponseView | null, errors: null as any };
      },
      delete: async (keys: { id: string; viewedAt?: string }) => {
        const result = await getClient().graphql({ query: deleteResponseView, variables: keys });
        return { data: (result as any).data?.deleteResponseView ?? null as ResponseView | null, errors: null as any };
      },
      listResponseViewsByPrompt: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listResponseViewsByPrompt, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listResponseViewsByPrompt;
        return { data: (conn?.items ?? []) as ResponseView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listResponseViewsByViewer: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listResponseViewsByViewer, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listResponseViewsByViewer;
        return { data: (conn?.items ?? []) as ResponseView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listResponseViewsByResponse: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listResponseViewsByResponse, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listResponseViewsByResponse;
        return { data: (conn?.items ?? []) as ResponseView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listListResponseViewsByPrompt() { return (this as any).listResponseViewsByPrompt; },
      get listListResponseViewsByViewer() { return (this as any).listResponseViewsByViewer; },
      get listListResponseViewsByResponse() { return (this as any).listResponseViewsByResponse; },
    },
    Reaction: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getReaction, variables: { id: id } });
        return { data: (result as any).data?.getReaction ?? null as Reaction | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listReactions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listReactions;
        return { data: (conn?.items ?? []) as Reaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createReaction, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createReaction ?? null as Reaction | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateReaction, variables: { input: input } });
        return { data: (result as any).data?.updateReaction ?? null as Reaction | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteReaction, variables: { id: id } });
        return { data: (result as any).data?.deleteReaction ?? null as Reaction | null, errors: null as any };
      },
      reactionByResponseId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: reactionByResponseId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.reactionByResponseId;
        return { data: (conn?.items ?? []) as Reaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      reactionByUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: reactionByUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.reactionByUserId;
        return { data: (conn?.items ?? []) as Reaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listReactionByResponseId() { return (this as any).reactionByResponseId; },
      get listReactionByUserId() { return (this as any).reactionByUserId; },
    },
    Conversation: {
      get: async (keys: { id: string; lastMessageAt?: string }) => {
        const result = await getClient().graphql({ query: getConversation, variables: keys });
        return { data: (result as any).data?.getConversation ?? null as Conversation | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listConversations, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listConversations;
        return { data: (conn?.items ?? []) as Conversation[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createConversation, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createConversation ?? null as Conversation | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateConversation, variables: { input: input } });
        return { data: (result as any).data?.updateConversation ?? null as Conversation | null, errors: null as any };
      },
      delete: async (keys: { id: string; lastMessageAt?: string }) => {
        const result = await getClient().graphql({ query: deleteConversation, variables: keys });
        return { data: (result as any).data?.deleteConversation ?? null as Conversation | null, errors: null as any };
      },
      listConversationsByType: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listConversationsByType, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listConversationsByType;
        return { data: (conn?.items ?? []) as Conversation[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      conversationsByCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: conversationsByCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.conversationsByCreator;
        return { data: (conn?.items ?? []) as Conversation[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listListConversationsByType() { return (this as any).listConversationsByType; },
      get listConversationsByCreator() { return (this as any).conversationsByCreator; },
    },
    ConversationArchive: {
      get: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: getConversationArchive, variables: keys });
        return { data: (result as any).data?.getConversationArchive ?? null as ConversationArchive | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listConversationArchives, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listConversationArchives;
        return { data: (conn?.items ?? []) as ConversationArchive[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createConversationArchive, variables: { input } });
        return { data: (result as any).data?.createConversationArchive ?? null as ConversationArchive | null, errors: null as any };
      },
      delete: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: deleteConversationArchive, variables: keys });
        return { data: (result as any).data?.deleteConversationArchive ?? null as ConversationArchive | null, errors: null as any };
      },
      archivesByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: archivesByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.archivesByUser;
        return { data: (conn?.items ?? []) as ConversationArchive[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    },
    GroupMembership: {
      get: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: getGroupMembership, variables: keys });
        return { data: (result as any).data?.getGroupMembership ?? null as GroupMembership | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listGroupMemberships, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listGroupMemberships;
        return { data: (conn?.items ?? []) as GroupMembership[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createGroupMembership, variables: { input: input } });
        return { data: (result as any).data?.createGroupMembership ?? null as GroupMembership | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateGroupMembership, variables: { input: input } });
        return { data: (result as any).data?.updateGroupMembership ?? null as GroupMembership | null, errors: null as any };
      },
      delete: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: deleteGroupMembership, variables: keys });
        return { data: (result as any).data?.deleteGroupMembership ?? null as GroupMembership | null, errors: null as any };
      },
      membershipsByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: membershipsByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.membershipsByUser;
        return { data: (conn?.items ?? []) as GroupMembership[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      membersByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: membersByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.membersByConversation;
        return { data: (conn?.items ?? []) as GroupMembership[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listMembershipsByUser() { return (this as any).membershipsByUser; },
      get listMembersByConversation() { return (this as any).membersByConversation; },
    },
    SystemEvent: {
      get: async (keys: { id: string; createdAt?: string }) => {
        const result = await getClient().graphql({ query: getSystemEvent, variables: keys });
        return { data: (result as any).data?.getSystemEvent ?? null as SystemEvent | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listSystemEvents, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listSystemEvents;
        return { data: (conn?.items ?? []) as SystemEvent[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createSystemEvent, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createSystemEvent ?? null as SystemEvent | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateSystemEvent, variables: { input: input } });
        return { data: (result as any).data?.updateSystemEvent ?? null as SystemEvent | null, errors: null as any };
      },
      delete: async (keys: { id: string; createdAt?: string }) => {
        const result = await getClient().graphql({ query: deleteSystemEvent, variables: keys });
        return { data: (result as any).data?.deleteSystemEvent ?? null as SystemEvent | null, errors: null as any };
      },
      eventsByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: eventsByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.eventsByConversation;
        return { data: (conn?.items ?? []) as SystemEvent[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listEventsByConversation() { return (this as any).eventsByConversation; },
    },
    GroupEncryptionKey: {
      get: async (keys: { id: string; version?: string }) => {
        const result = await getClient().graphql({ query: getGroupEncryptionKey, variables: keys });
        return { data: (result as any).data?.getGroupEncryptionKey ?? null as GroupEncryptionKey | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listGroupEncryptionKeys, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listGroupEncryptionKeys;
        return { data: (conn?.items ?? []) as GroupEncryptionKey[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createGroupEncryptionKey, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createGroupEncryptionKey ?? null as GroupEncryptionKey | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateGroupEncryptionKey, variables: { input: input } });
        return { data: (result as any).data?.updateGroupEncryptionKey ?? null as GroupEncryptionKey | null, errors: null as any };
      },
      delete: async (keys: { id: string; version?: string }) => {
        const result = await getClient().graphql({ query: deleteGroupEncryptionKey, variables: keys });
        return { data: (result as any).data?.deleteGroupEncryptionKey ?? null as GroupEncryptionKey | null, errors: null as any };
      },
      keysByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: keysByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.keysByConversation;
        return { data: (conn?.items ?? []) as GroupEncryptionKey[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listKeysByConversation() { return (this as any).keysByConversation; },
    },
    GroupPrompt: {
      get: async (keys: { id: string; createdAt?: string }) => {
        const result = await getClient().graphql({ query: getGroupPrompt, variables: keys });
        return { data: (result as any).data?.getGroupPrompt ?? null as GroupPrompt | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listGroupPrompts, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listGroupPrompts;
        return { data: (conn?.items ?? []) as GroupPrompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createGroupPrompt, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createGroupPrompt ?? null as GroupPrompt | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateGroupPrompt, variables: { input: input } });
        return { data: (result as any).data?.updateGroupPrompt ?? null as GroupPrompt | null, errors: null as any };
      },
      delete: async (keys: { id: string; createdAt?: string }) => {
        const result = await getClient().graphql({ query: deleteGroupPrompt, variables: keys });
        return { data: (result as any).data?.deleteGroupPrompt ?? null as GroupPrompt | null, errors: null as any };
      },
      promptsByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: promptsByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.promptsByConversation;
        return { data: (conn?.items ?? []) as GroupPrompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      groupsByPrompt: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: groupsByPrompt, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.groupsByPrompt;
        return { data: (conn?.items ?? []) as GroupPrompt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listPromptsByConversation() { return (this as any).promptsByConversation; },
      get listGroupsByPrompt() { return (this as any).groupsByPrompt; },
    },
    Message: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getMessage, variables: { id: id } });
        return { data: (result as any).data?.getMessage ?? null as Message | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listMessages, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listMessages;
        return { data: (conn?.items ?? []) as Message[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createMessage, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createMessage ?? null as Message | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateMessage, variables: { input: input } });
        return { data: (result as any).data?.updateMessage ?? null as Message | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteMessage, variables: { id: id } });
        return { data: (result as any).data?.deleteMessage ?? null as Message | null, errors: null as any };
      },
      messageByConversationId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: messageByConversationId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.messageByConversationId;
        return { data: (conn?.items ?? []) as Message[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      legacyMessagesByExpiry: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: legacyMessagesByExpiry, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.legacyMessagesByExpiry;
        return { data: (conn?.items ?? []) as Message[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listMessageByConversationId() { return (this as any).messageByConversationId; },
      get listLegacyMessagesByExpiry() { return (this as any).legacyMessagesByExpiry; },
    },
    EncryptedMessage: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getEncryptedMessage, variables: { id: id } });
        return { data: (result as any).data?.getEncryptedMessage ?? null as EncryptedMessage | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listEncryptedMessages, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listEncryptedMessages;
        return { data: (conn?.items ?? []) as EncryptedMessage[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createEncryptedMessage, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createEncryptedMessage ?? null as EncryptedMessage | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateEncryptedMessage, variables: { input: input } });
        return { data: (result as any).data?.updateEncryptedMessage ?? null as EncryptedMessage | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteEncryptedMessage, variables: { id: id } });
        return { data: (result as any).data?.deleteEncryptedMessage ?? null as EncryptedMessage | null, errors: null as any };
      },
      encryptedMessageByConversationId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: encryptedMessageByConversationId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.encryptedMessageByConversationId;
        return { data: (conn?.items ?? []) as EncryptedMessage[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      encryptedMessageByRecipientDeviceId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: encryptedMessageByRecipientDeviceId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.encryptedMessageByRecipientDeviceId;
        return { data: (conn?.items ?? []) as EncryptedMessage[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      encryptedMessageByMessageGroupId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: encryptedMessageByMessageGroupId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.encryptedMessageByMessageGroupId;
        return { data: (conn?.items ?? []) as EncryptedMessage[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      messagesByExpiry: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: messagesByExpiry, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.messagesByExpiry;
        return { data: (conn?.items ?? []) as EncryptedMessage[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listEncryptedMessageByConversationId() { return (this as any).encryptedMessageByConversationId; },
      get listEncryptedMessageByRecipientDeviceId() { return (this as any).encryptedMessageByRecipientDeviceId; },
      get listEncryptedMessageByMessageGroupId() { return (this as any).encryptedMessageByMessageGroupId; },
      get listMessagesByExpiry() { return (this as any).messagesByExpiry; },
    },
    MessageReaction: {
      get: async (idOrObj: string | { id: string }) => {
        try {
          const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
          const result = await getClient().graphql({ query: getMessageReaction, variables: { id: id } });
          return { data: (result as any).data?.getMessageReaction ?? null as MessageReaction | null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.get failed:', err);
          return { data: null as MessageReaction | null, errors: err.errors || [err] };
        }
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        try {
          const result = await getClient().graphql({ query: listMessageReactions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
          const conn = (result as any).data?.listMessageReactions;
          return { data: (conn?.items ?? []) as MessageReaction[], nextToken: conn?.nextToken ?? null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.list failed:', err);
          return { data: [] as MessageReaction[], nextToken: null, errors: err.errors || [err] };
        }
      },
      create: async (input: any) => {
        try {
          const result = await getClient().graphql({ query: createMessageReaction, variables: { input: ensureId(input) } });
          return { data: (result as any).data?.createMessageReaction ?? null as MessageReaction | null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.create failed:', err);
          return { data: null as MessageReaction | null, errors: err.errors || [err] };
        }
      },
      update: async (input: any) => {
        try {
          const result = await getClient().graphql({ query: updateMessageReaction, variables: { input: input } });
          return { data: (result as any).data?.updateMessageReaction ?? null as MessageReaction | null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.update failed:', err);
          return { data: null as MessageReaction | null, errors: err.errors || [err] };
        }
      },
      delete: async (idOrObj: string | { id: string }) => {
        try {
          const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
          const result = await getClient().graphql({ query: deleteMessageReaction, variables: { id: id } });
          return { data: (result as any).data?.deleteMessageReaction ?? null as MessageReaction | null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.delete failed:', err);
          return { data: null as MessageReaction | null, errors: err.errors || [err] };
        }
      },
      messageReactionByMessageId: async (...args: any[]) => {
        try {
          const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
          const result = await getClient().graphql({ query: messageReactionByMessageId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
          const conn = (result as any).data?.messageReactionByMessageId;
          return { data: (conn?.items ?? []) as MessageReaction[], nextToken: conn?.nextToken ?? null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.messageReactionByMessageId failed:', err);
          return { data: [] as MessageReaction[], nextToken: null, errors: err.errors || [err] };
        }
      },
      messageReactionByMessageGroupId: async (...args: any[]) => {
        try {
          const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
          const result = await getClient().graphql({ query: messageReactionByMessageGroupId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
          const conn = (result as any).data?.messageReactionByMessageGroupId;
          return { data: (conn?.items ?? []) as MessageReaction[], nextToken: conn?.nextToken ?? null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.messageReactionByMessageGroupId failed:', err);
          return { data: [] as MessageReaction[], nextToken: null, errors: err.errors || [err] };
        }
      },
      messageReactionByConversationId: async (...args: any[]) => {
        try {
          const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
          const result = await getClient().graphql({ query: messageReactionByConversationId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
          const conn = (result as any).data?.messageReactionByConversationId;
          return { data: (conn?.items ?? []) as MessageReaction[], nextToken: conn?.nextToken ?? null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.messageReactionByConversationId failed:', err);
          return { data: [] as MessageReaction[], nextToken: null, errors: err.errors || [err] };
        }
      },
      reactionsByExpiry: async (...args: any[]) => {
        try {
          const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
          const result = await getClient().graphql({ query: reactionsByExpiry, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
          const conn = (result as any).data?.reactionsByExpiry;
          return { data: (conn?.items ?? []) as MessageReaction[], nextToken: conn?.nextToken ?? null, errors: null as any[] | null };
        } catch (err: any) {
          console.error('[cdkModelsClient] MessageReaction.reactionsByExpiry failed:', err);
          return { data: [] as MessageReaction[], nextToken: null, errors: err.errors || [err] };
        }
      },

      get listMessageReactionByMessageId() { return (this as any).messageReactionByMessageId; },
      get listMessageReactionByMessageGroupId() { return (this as any).messageReactionByMessageGroupId; },
      get listMessageReactionByConversationId() { return (this as any).messageReactionByConversationId; },
      get listReactionsByExpiry() { return (this as any).reactionsByExpiry; },
    },
    UnsolicitedMessageTracker: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getUnsolicitedMessageTracker, variables: { id: id } });
        return { data: (result as any).data?.getUnsolicitedMessageTracker ?? null as UnsolicitedMessageTracker | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listUnsolicitedMessageTrackers, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listUnsolicitedMessageTrackers;
        return { data: (conn?.items ?? []) as UnsolicitedMessageTracker[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createUnsolicitedMessageTracker, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createUnsolicitedMessageTracker ?? null as UnsolicitedMessageTracker | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateUnsolicitedMessageTracker, variables: { input: input } });
        return { data: (result as any).data?.updateUnsolicitedMessageTracker ?? null as UnsolicitedMessageTracker | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteUnsolicitedMessageTracker, variables: { id: id } });
        return { data: (result as any).data?.deleteUnsolicitedMessageTracker ?? null as UnsolicitedMessageTracker | null, errors: null as any };
      },
      unsolicitedMessageTrackerBySenderId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: unsolicitedMessageTrackerBySenderId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.unsolicitedMessageTrackerBySenderId;
        return { data: (conn?.items ?? []) as UnsolicitedMessageTracker[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listUnsolicitedMessageTrackerBySenderId() { return (this as any).unsolicitedMessageTrackerBySenderId; },
    },
    BlockList: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getBlockList, variables: { id: id } });
        return { data: (result as any).data?.getBlockList ?? null as BlockList | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listBlockLists, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listBlockLists;
        return { data: (conn?.items ?? []) as BlockList[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createBlockList, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createBlockList ?? null as BlockList | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateBlockList, variables: { input: input } });
        return { data: (result as any).data?.updateBlockList ?? null as BlockList | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteBlockList, variables: { id: id } });
        return { data: (result as any).data?.deleteBlockList ?? null as BlockList | null, errors: null as any };
      },
      blockListByBlockerUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: blockListByBlockerUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.blockListByBlockerUserId;
        return { data: (conn?.items ?? []) as BlockList[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      blockListByBlockedUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: blockListByBlockedUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.blockListByBlockedUserId;
        return { data: (conn?.items ?? []) as BlockList[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listBlockListByBlockerUserId() { return (this as any).blockListByBlockerUserId; },
      get listBlockListByBlockedUserId() { return (this as any).blockListByBlockedUserId; },
    },
    MuteList: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getMuteList, variables: { id: id } });
        return { data: (result as any).data?.getMuteList ?? null as MuteList | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listMuteLists, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listMuteLists;
        return { data: (conn?.items ?? []) as MuteList[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createMuteList, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createMuteList ?? null as MuteList | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateMuteList, variables: { input: input } });
        return { data: (result as any).data?.updateMuteList ?? null as MuteList | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteMuteList, variables: { id: id } });
        return { data: (result as any).data?.deleteMuteList ?? null as MuteList | null, errors: null as any };
      },
      muteListByMuterUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: muteListByMuterUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.muteListByMuterUserId;
        return { data: (conn?.items ?? []) as MuteList[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      muteListByMutedUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: muteListByMutedUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.muteListByMutedUserId;
        return { data: (conn?.items ?? []) as MuteList[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },

      get listMuteListByMuterUserId() { return (this as any).muteListByMuterUserId; },
      get listMuteListByMutedUserId() { return (this as any).muteListByMutedUserId; },
    },
    ReadReceipt: {
      get: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: getReadReceipt, variables: keys });
        return { data: (result as any).data?.getReadReceipt ?? null as ReadReceipt | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listReadReceipts, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listReadReceipts;
        return { data: (conn?.items ?? []) as ReadReceipt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createReadReceipt, variables: { input: input } });
        return { data: (result as any).data?.createReadReceipt ?? null as ReadReceipt | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateReadReceipt, variables: { input: input } });
        return { data: (result as any).data?.updateReadReceipt ?? null as ReadReceipt | null, errors: null as any };
      },
      delete: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: deleteReadReceipt, variables: keys });
        return { data: (result as any).data?.deleteReadReceipt ?? null as ReadReceipt | null, errors: null as any };
      },
      readReceiptByConversationId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: readReceiptByConversationId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.readReceiptByConversationId;
        return { data: (conn?.items ?? []) as ReadReceipt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      readReceiptByUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: readReceiptByUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.readReceiptByUserId;
        return { data: (conn?.items ?? []) as ReadReceipt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listReadReceiptByConversationId() { return (this as any).readReceiptByConversationId; },
      get listReadReceiptByUserId() { return (this as any).readReceiptByUserId; },
    },
    TypingIndicator: {
      get: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: getTypingIndicator, variables: keys });
        return { data: (result as any).data?.getTypingIndicator ?? null as TypingIndicator | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listTypingIndicators, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listTypingIndicators;
        return { data: (conn?.items ?? []) as TypingIndicator[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createTypingIndicator, variables: { input: input } });
        return { data: (result as any).data?.createTypingIndicator ?? null as TypingIndicator | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateTypingIndicator, variables: { input: input } });
        return { data: (result as any).data?.updateTypingIndicator ?? null as TypingIndicator | null, errors: null as any };
      },
      delete: async (keys: { conversationId: string; userId: string }) => {
        const result = await getClient().graphql({ query: deleteTypingIndicator, variables: keys });
        return { data: (result as any).data?.deleteTypingIndicator ?? null as TypingIndicator | null, errors: null as any };
      },
      typingByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: typingByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.typingByConversation;
        return { data: (conn?.items ?? []) as TypingIndicator[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listTypingByConversation() { return (this as any).typingByConversation; },
    },
    MessageMention: {
      get: async (keys: { id: string; createdAt?: string }) => {
        const result = await getClient().graphql({ query: getMessageMention, variables: keys });
        return { data: (result as any).data?.getMessageMention ?? null as MessageMention | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listMessageMentions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listMessageMentions;
        return { data: (conn?.items ?? []) as MessageMention[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createMessageMention, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createMessageMention ?? null as MessageMention | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateMessageMention, variables: { input: input } });
        return { data: (result as any).data?.updateMessageMention ?? null as MessageMention | null, errors: null as any };
      },
      delete: async (keys: { id: string; createdAt?: string }) => {
        const result = await getClient().graphql({ query: deleteMessageMention, variables: keys });
        return { data: (result as any).data?.deleteMessageMention ?? null as MessageMention | null, errors: null as any };
      },
      mentionsByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: mentionsByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.mentionsByUser;
        return { data: (conn?.items ?? []) as MessageMention[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      mentionsByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: mentionsByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.mentionsByConversation;
        return { data: (conn?.items ?? []) as MessageMention[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listMentionsByUser() { return (this as any).mentionsByUser; },
      get listMentionsByConversation() { return (this as any).mentionsByConversation; },
    },
    PinnedMessage: {
      get: async (keys: { conversationId: string; messageId: string }) => {
        const result = await getClient().graphql({ query: getPinnedMessage, variables: keys });
        return { data: (result as any).data?.getPinnedMessage ?? null as PinnedMessage | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPinnedMessages, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPinnedMessages;
        return { data: (conn?.items ?? []) as PinnedMessage[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPinnedMessage, variables: { input: input } });
        return { data: (result as any).data?.createPinnedMessage ?? null as PinnedMessage | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePinnedMessage, variables: { input: input } });
        return { data: (result as any).data?.updatePinnedMessage ?? null as PinnedMessage | null, errors: null as any };
      },
      delete: async (keys: { conversationId: string; messageId: string }) => {
        const result = await getClient().graphql({ query: deletePinnedMessage, variables: keys });
        return { data: (result as any).data?.deletePinnedMessage ?? null as PinnedMessage | null, errors: null as any };
      },
      pinnedByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: pinnedByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.pinnedByConversation;
        return { data: (conn?.items ?? []) as PinnedMessage[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listPinnedByConversation() { return (this as any).pinnedByConversation; },
    },
    Poll: {
      get: async (keys: { id: string; pollCreatedAt?: string }) => {
        const result = await getClient().graphql({ query: getPoll, variables: keys });
        return { data: (result as any).data?.getPoll ?? null as Poll | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPolls, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPolls;
        return { data: (conn?.items ?? []) as Poll[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPoll, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createPoll ?? null as Poll | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePoll, variables: { input: input } });
        return { data: (result as any).data?.updatePoll ?? null as Poll | null, errors: null as any };
      },
      delete: async (keys: { id: string; pollCreatedAt?: string }) => {
        const result = await getClient().graphql({ query: deletePoll, variables: keys });
        return { data: (result as any).data?.deletePoll ?? null as Poll | null, errors: null as any };
      },
      pollsByConversation: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: pollsByConversation, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.pollsByConversation;
        return { data: (conn?.items ?? []) as Poll[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      pollsByAudience: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: pollsByAudience, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.pollsByAudience;
        return { data: (conn?.items ?? []) as Poll[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      pollsByCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: pollsByCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.pollsByCreator;
        return { data: (conn?.items ?? []) as Poll[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },

      get listPollsByConversation() { return (this as any).pollsByConversation; },
      get listPollsByAudience() { return (this as any).pollsByAudience; },
      get listPollsByCreator() { return (this as any).pollsByCreator; },
    },
    Flag: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getFlag, variables: { id: id } });
        return { data: (result as any).data?.getFlag ?? null as Flag | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listFlags, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listFlags;
        return { data: (conn?.items ?? []) as Flag[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createFlag, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createFlag ?? null as Flag | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateFlag, variables: { input: input } });
        return { data: (result as any).data?.updateFlag ?? null as Flag | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteFlag, variables: { id: id } });
        return { data: (result as any).data?.deleteFlag ?? null as Flag | null, errors: null as any };
      },
      flagByContentId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: flagByContentId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.flagByContentId;
        return { data: (conn?.items ?? []) as Flag[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      flagByReporterId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: flagByReporterId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.flagByReporterId;
        return { data: (conn?.items ?? []) as Flag[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      flagByReporterContentKey: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: flagByReporterContentKey, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.flagByReporterContentKey;
        return { data: (conn?.items ?? []) as Flag[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      flagByStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: flagByStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.flagByStatus;
        return { data: (conn?.items ?? []) as Flag[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listFlagByContentId() { return (this as any).flagByContentId; },
      get listFlagByReporterId() { return (this as any).flagByReporterId; },
      get listFlagByReporterContentKey() { return (this as any).flagByReporterContentKey; },
      get listFlagByStatus() { return (this as any).flagByStatus; },
    },
    ModerationCase: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getModerationCase, variables: { id: id } });
        return { data: (result as any).data?.getModerationCase ?? null as ModerationCase | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listModerationCases, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listModerationCases;
        return { data: (conn?.items ?? []) as ModerationCase[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createModerationCase, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createModerationCase ?? null as ModerationCase | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateModerationCase, variables: { input: input } });
        return { data: (result as any).data?.updateModerationCase ?? null as ModerationCase | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteModerationCase, variables: { id: id } });
        return { data: (result as any).data?.deleteModerationCase ?? null as ModerationCase | null, errors: null as any };
      },
      moderationCaseByContentId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: moderationCaseByContentId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.moderationCaseByContentId;
        return { data: (conn?.items ?? []) as ModerationCase[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      moderationCaseByCaseStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: moderationCaseByCaseStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.moderationCaseByCaseStatus;
        return { data: (conn?.items ?? []) as ModerationCase[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      moderationCaseByAssignedTo: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: moderationCaseByAssignedTo, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.moderationCaseByAssignedTo;
        return { data: (conn?.items ?? []) as ModerationCase[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      moderationCaseByContentOwnerId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: moderationCaseByContentOwnerId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.moderationCaseByContentOwnerId;
        return { data: (conn?.items ?? []) as ModerationCase[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listModerationCaseByContentId() { return (this as any).moderationCaseByContentId; },
      get listModerationCaseByCaseStatus() { return (this as any).moderationCaseByCaseStatus; },
      get listModerationCaseByAssignedTo() { return (this as any).moderationCaseByAssignedTo; },
      get listModerationCaseByContentOwnerId() { return (this as any).moderationCaseByContentOwnerId; },
    },
    ModerationAction: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getModerationAction, variables: { id: id } });
        return { data: (result as any).data?.getModerationAction ?? null as ModerationAction | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listModerationActions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listModerationActions;
        return { data: (conn?.items ?? []) as ModerationAction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createModerationAction, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createModerationAction ?? null as ModerationAction | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateModerationAction, variables: { input: input } });
        return { data: (result as any).data?.updateModerationAction ?? null as ModerationAction | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteModerationAction, variables: { id: id } });
        return { data: (result as any).data?.deleteModerationAction ?? null as ModerationAction | null, errors: null as any };
      },
      moderationActionByCaseId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: moderationActionByCaseId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.moderationActionByCaseId;
        return { data: (conn?.items ?? []) as ModerationAction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      moderationActionByActorId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: moderationActionByActorId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.moderationActionByActorId;
        return { data: (conn?.items ?? []) as ModerationAction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listModerationActionByCaseId() { return (this as any).moderationActionByCaseId; },
      get listModerationActionByActorId() { return (this as any).moderationActionByActorId; },
    },
    Story: {
      get: async (keys: { id: string; expiresAt?: string }) => {
        const result = await getClient().graphql({ query: getStory, variables: keys });
        return { data: (result as any).data?.getStory ?? null as Story | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listStories, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listStories;
        return { data: (conn?.items ?? []) as Story[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createStory, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createStory ?? null as Story | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateStory, variables: { input: input } });
        return { data: (result as any).data?.updateStory ?? null as Story | null, errors: null as any };
      },
      delete: async (keys: { id: string; expiresAt?: string }) => {
        const result = await getClient().graphql({ query: deleteStory, variables: keys });
        return { data: (result as any).data?.deleteStory ?? null as Story | null, errors: null as any };
      },
      listStoriesByCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listStoriesByCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listStoriesByCreator;
        return { data: (conn?.items ?? []) as Story[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listStoriesByExpiry: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listStoriesByExpiry, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listStoriesByExpiry;
        return { data: (conn?.items ?? []) as Story[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listStoriesByVisibility: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listStoriesByVisibility, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listStoriesByVisibility;
        return { data: (conn?.items ?? []) as Story[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listListStoriesByCreator() { return (this as any).listStoriesByCreator; },
      get listListStoriesByExpiry() { return (this as any).listStoriesByExpiry; },
      get listListStoriesByVisibility() { return (this as any).listStoriesByVisibility; },
    },
    StorySegment: {
      get: async (keys: { id: string; displayOrder?: string }) => {
        const result = await getClient().graphql({ query: getStorySegment, variables: keys });
        return { data: (result as any).data?.getStorySegment ?? null as StorySegment | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listStorySegments, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listStorySegments;
        return { data: (conn?.items ?? []) as StorySegment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createStorySegment, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createStorySegment ?? null as StorySegment | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateStorySegment, variables: { input: input } });
        return { data: (result as any).data?.updateStorySegment ?? null as StorySegment | null, errors: null as any };
      },
      delete: async (keys: { id: string; displayOrder?: string }) => {
        const result = await getClient().graphql({ query: deleteStorySegment, variables: keys });
        return { data: (result as any).data?.deleteStorySegment ?? null as StorySegment | null, errors: null as any };
      },
      listSegmentsByStory: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listSegmentsByStory, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listSegmentsByStory;
        return { data: (conn?.items ?? []) as StorySegment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listSegmentsByCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listSegmentsByCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listSegmentsByCreator;
        return { data: (conn?.items ?? []) as StorySegment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listListSegmentsByStory() { return (this as any).listSegmentsByStory; },
      get listListSegmentsByCreator() { return (this as any).listSegmentsByCreator; },
    },
    StoryView: {
      get: async (keys: { id: string; viewedAt?: string }) => {
        const result = await getClient().graphql({ query: getStoryView, variables: keys });
        return { data: (result as any).data?.getStoryView ?? null as StoryView | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listStoryViews, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listStoryViews;
        return { data: (conn?.items ?? []) as StoryView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createStoryView, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createStoryView ?? null as StoryView | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateStoryView, variables: { input: input } });
        return { data: (result as any).data?.updateStoryView ?? null as StoryView | null, errors: null as any };
      },
      delete: async (keys: { id: string; viewedAt?: string }) => {
        const result = await getClient().graphql({ query: deleteStoryView, variables: keys });
        return { data: (result as any).data?.deleteStoryView ?? null as StoryView | null, errors: null as any };
      },
      listViewsByStory: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listViewsByStory, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listViewsByStory;
        return { data: (conn?.items ?? []) as StoryView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listViewsByViewer: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listViewsByViewer, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listViewsByViewer;
        return { data: (conn?.items ?? []) as StoryView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listViewsBySegment: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listViewsBySegment, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listViewsBySegment;
        return { data: (conn?.items ?? []) as StoryView[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listListViewsByStory() { return (this as any).listViewsByStory; },
      get listListViewsByViewer() { return (this as any).listViewsByViewer; },
      get listListViewsBySegment() { return (this as any).listViewsBySegment; },
    },
    StoryReaction: {
      get: async (keys: { id: string; reactorUserId?: string }) => {
        const result = await getClient().graphql({ query: getStoryReaction, variables: keys });
        return { data: (result as any).data?.getStoryReaction ?? null as StoryReaction | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listStoryReactions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listStoryReactions;
        return { data: (conn?.items ?? []) as StoryReaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createStoryReaction, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createStoryReaction ?? null as StoryReaction | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateStoryReaction, variables: { input: input } });
        return { data: (result as any).data?.updateStoryReaction ?? null as StoryReaction | null, errors: null as any };
      },
      delete: async (keys: { id: string; reactorUserId?: string }) => {
        const result = await getClient().graphql({ query: deleteStoryReaction, variables: keys });
        return { data: (result as any).data?.deleteStoryReaction ?? null as StoryReaction | null, errors: null as any };
      },
      listReactionsByStory: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listReactionsByStory, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listReactionsByStory;
        return { data: (conn?.items ?? []) as StoryReaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listReactionsBySegment: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listReactionsBySegment, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listReactionsBySegment;
        return { data: (conn?.items ?? []) as StoryReaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listReactionsByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listReactionsByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listReactionsByUser;
        return { data: (conn?.items ?? []) as StoryReaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      listReactionsByStoryAndUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listReactionsByStoryAndUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listReactionsByStoryAndUser;
        return { data: (conn?.items ?? []) as StoryReaction[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listListReactionsByStory() { return (this as any).listReactionsByStory; },
      get listListReactionsBySegment() { return (this as any).listReactionsBySegment; },
      get listListReactionsByUser() { return (this as any).listReactionsByUser; },
      get listListReactionsByStoryAndUser() { return (this as any).listReactionsByStoryAndUser; },
    },
    CloseFriend: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getCloseFriend, variables: { id: id } });
        return { data: (result as any).data?.getCloseFriend ?? null as CloseFriend | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listCloseFriends, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listCloseFriends;
        return { data: (conn?.items ?? []) as CloseFriend[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createCloseFriend, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createCloseFriend ?? null as CloseFriend | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateCloseFriend, variables: { input: input } });
        return { data: (result as any).data?.updateCloseFriend ?? null as CloseFriend | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteCloseFriend, variables: { id: id } });
        return { data: (result as any).data?.deleteCloseFriend ?? null as CloseFriend | null, errors: null as any };
      },
      closeFriendsByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: closeFriendsByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.closeFriendsByUser;
        return { data: (conn?.items ?? []) as CloseFriend[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      closeFriendsByFriend: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: closeFriendsByFriend, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.closeFriendsByFriend;
        return { data: (conn?.items ?? []) as CloseFriend[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listCloseFriendsByUser() { return (this as any).closeFriendsByUser; },
      get listCloseFriendsByFriend() { return (this as any).closeFriendsByFriend; },
    },
    StoryMute: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getStoryMute, variables: { id: id } });
        return { data: (result as any).data?.getStoryMute ?? null as StoryMute | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listStoryMutes, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listStoryMutes;
        return { data: (conn?.items ?? []) as StoryMute[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createStoryMute, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createStoryMute ?? null as StoryMute | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateStoryMute, variables: { input: input } });
        return { data: (result as any).data?.updateStoryMute ?? null as StoryMute | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteStoryMute, variables: { id: id } });
        return { data: (result as any).data?.deleteStoryMute ?? null as StoryMute | null, errors: null as any };
      },
      storyMutesByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: storyMutesByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.storyMutesByUser;
        return { data: (conn?.items ?? []) as StoryMute[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listStoryMutesByUser() { return (this as any).storyMutesByUser; },
    },
    StoryTopRowFavorite: {
      get: async (keys: { id: string; position?: string }) => {
        const result = await getClient().graphql({ query: getStoryTopRowFavorite, variables: keys });
        return { data: (result as any).data?.getStoryTopRowFavorite ?? null as StoryTopRowFavorite | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listStoryTopRowFavorites, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listStoryTopRowFavorites;
        return { data: (conn?.items ?? []) as StoryTopRowFavorite[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createStoryTopRowFavorite, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createStoryTopRowFavorite ?? null as StoryTopRowFavorite | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateStoryTopRowFavorite, variables: { input: input } });
        return { data: (result as any).data?.updateStoryTopRowFavorite ?? null as StoryTopRowFavorite | null, errors: null as any };
      },
      delete: async (keys: { id: string; position?: string }) => {
        const result = await getClient().graphql({ query: deleteStoryTopRowFavorite, variables: keys });
        return { data: (result as any).data?.deleteStoryTopRowFavorite ?? null as StoryTopRowFavorite | null, errors: null as any };
      },
      topRowFavoritesByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: topRowFavoritesByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.topRowFavoritesByUser;
        return { data: (conn?.items ?? []) as StoryTopRowFavorite[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      topRowFavoritesByFavorite: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: topRowFavoritesByFavorite, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.topRowFavoritesByFavorite;
        return { data: (conn?.items ?? []) as StoryTopRowFavorite[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listTopRowFavoritesByUser() { return (this as any).topRowFavoritesByUser; },
      get listTopRowFavoritesByFavorite() { return (this as any).topRowFavoritesByFavorite; },
    },
    PromptSuggestion: {
      get: async (keys: { id: string; usageCount?: string }) => {
        const result = await getClient().graphql({ query: getPromptSuggestion, variables: keys });
        return { data: (result as any).data?.getPromptSuggestion ?? null as PromptSuggestion | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPromptSuggestions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPromptSuggestions;
        return { data: (conn?.items ?? []) as PromptSuggestion[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPromptSuggestion, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createPromptSuggestion ?? null as PromptSuggestion | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePromptSuggestion, variables: { input: input } });
        return { data: (result as any).data?.updatePromptSuggestion ?? null as PromptSuggestion | null, errors: null as any };
      },
      delete: async (keys: { id: string; usageCount?: string }) => {
        const result = await getClient().graphql({ query: deletePromptSuggestion, variables: keys });
        return { data: (result as any).data?.deletePromptSuggestion ?? null as PromptSuggestion | null, errors: null as any };
      },
      suggestionsByCategory: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: suggestionsByCategory, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.suggestionsByCategory;
        return { data: (conn?.items ?? []) as PromptSuggestion[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      suggestionsByVisibility: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: suggestionsByVisibility, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.suggestionsByVisibility;
        return { data: (conn?.items ?? []) as PromptSuggestion[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      suggestionsByLanguage: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: suggestionsByLanguage, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.suggestionsByLanguage;
        return { data: (conn?.items ?? []) as PromptSuggestion[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      suggestionsByModerationStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: suggestionsByModerationStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.suggestionsByModerationStatus;
        return { data: (conn?.items ?? []) as PromptSuggestion[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      suggestionsByCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: suggestionsByCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.suggestionsByCreator;
        return { data: (conn?.items ?? []) as PromptSuggestion[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      automationEligibleSuggestions: async (opts: { automationStatus: string; usageCount?: number; filter?: any; limit?: number; nextToken?: string; sortDirection?: string }) => {
        const result = await getClient().graphql({ query: automationEligibleSuggestions, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.automationEligibleSuggestions;
        return { data: (conn?.items ?? []) as PromptSuggestion[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listSuggestionsByCategory() { return (this as any).suggestionsByCategory; },
      get listSuggestionsByVisibility() { return (this as any).suggestionsByVisibility; },
      get listSuggestionsByLanguage() { return (this as any).suggestionsByLanguage; },
      get listSuggestionsByModerationStatus() { return (this as any).suggestionsByModerationStatus; },
      get listSuggestionsByCreator() { return (this as any).suggestionsByCreator; },
    },
    AutomationRule: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getAutomationRule, variables: { id: id } });
        return { data: (result as any).data?.getAutomationRule ?? null as AutomationRule | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listAutomationRules, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listAutomationRules;
        return { data: (conn?.items ?? []) as AutomationRule[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createAutomationRule, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createAutomationRule ?? null as AutomationRule | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateAutomationRule, variables: { input: input } });
        return { data: (result as any).data?.updateAutomationRule ?? null as AutomationRule | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteAutomationRule, variables: { id: id } });
        return { data: (result as any).data?.deleteAutomationRule ?? null as AutomationRule | null, errors: null as any };
      },
      automationRuleByPersonaId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: automationRuleByPersonaId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.automationRuleByPersonaId;
        return { data: (conn?.items ?? []) as AutomationRule[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      automationRuleByEnabledStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: automationRuleByEnabledStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.automationRuleByEnabledStatus;
        return { data: (conn?.items ?? []) as AutomationRule[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      automationRuleBySourceType: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: automationRuleBySourceType, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.automationRuleBySourceType;
        return { data: (conn?.items ?? []) as AutomationRule[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listAutomationRuleByPersonaId() { return (this as any).automationRuleByPersonaId; },
      get listAutomationRuleByEnabledStatus() { return (this as any).automationRuleByEnabledStatus; },
      get listAutomationRuleBySourceType() { return (this as any).automationRuleBySourceType; },
    },
    AutomationExecution: {
      get: async (keys: { id: string; executionStartedAt?: string }) => {
        const result = await getClient().graphql({ query: getAutomationExecution, variables: keys });
        return { data: (result as any).data?.getAutomationExecution ?? null as AutomationExecution | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listAutomationExecutions, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listAutomationExecutions;
        return { data: (conn?.items ?? []) as AutomationExecution[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createAutomationExecution, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createAutomationExecution ?? null as AutomationExecution | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateAutomationExecution, variables: { input: input } });
        return { data: (result as any).data?.updateAutomationExecution ?? null as AutomationExecution | null, errors: null as any };
      },
      delete: async (keys: { id: string; executionStartedAt?: string }) => {
        const result = await getClient().graphql({ query: deleteAutomationExecution, variables: keys });
        return { data: (result as any).data?.deleteAutomationExecution ?? null as AutomationExecution | null, errors: null as any };
      },
      executionsByRule: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: executionsByRule, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.executionsByRule;
        return { data: (conn?.items ?? []) as AutomationExecution[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      executionsByPersona: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: executionsByPersona, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.executionsByPersona;
        return { data: (conn?.items ?? []) as AutomationExecution[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      executionsByStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: executionsByStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.executionsByStatus;
        return { data: (conn?.items ?? []) as AutomationExecution[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      executionsByPrompt: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: executionsByPrompt, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.executionsByPrompt;
        return { data: (conn?.items ?? []) as AutomationExecution[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listExecutionsByRule() { return (this as any).executionsByRule; },
      get listExecutionsByPersona() { return (this as any).executionsByPersona; },
      get listExecutionsByStatus() { return (this as any).executionsByStatus; },
      get listExecutionsByPrompt() { return (this as any).executionsByPrompt; },
    },
    AgeVerification: {
      get: async (idOrObj: any) => {
        const userId = typeof idOrObj === 'string' ? idOrObj : (idOrObj.userId || idOrObj.id);
        const result = await getClient().graphql({ query: getAgeVerification, variables: { userId } });
        return { data: (result as any).data?.getAgeVerification ?? null as AgeVerification | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listAgeVerifications, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listAgeVerifications;
        return { data: (conn?.items ?? []) as AgeVerification[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createAgeVerification, variables: { input: input } });
        return { data: (result as any).data?.createAgeVerification ?? null as AgeVerification | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateAgeVerification, variables: { input: input } });
        return { data: (result as any).data?.updateAgeVerification ?? null as AgeVerification | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteAgeVerification, variables: { userId: id } });
        return { data: (result as any).data?.deleteAgeVerification ?? null as AgeVerification | null, errors: null as any };
      },
      ageVerificationsByStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: ageVerificationsByStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.ageVerificationsByStatus;
        return { data: (conn?.items ?? []) as AgeVerification[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      ageVerificationsByDocHash: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: ageVerificationsByDocHash, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.ageVerificationsByDocHash;
        return { data: (conn?.items ?? []) as AgeVerification[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listAgeVerificationsByStatus() { return (this as any).ageVerificationsByStatus; },
      get listAgeVerificationsByDocHash() { return (this as any).ageVerificationsByDocHash; },
    },
    VerificationAttempt: {
      get: async (keys: { id: string; startedAt?: string }) => {
        const result = await getClient().graphql({ query: getVerificationAttempt, variables: keys });
        return { data: (result as any).data?.getVerificationAttempt ?? null as VerificationAttempt | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listVerificationAttempts, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listVerificationAttempts;
        return { data: (conn?.items ?? []) as VerificationAttempt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createVerificationAttempt, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createVerificationAttempt ?? null as VerificationAttempt | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateVerificationAttempt, variables: { input: input } });
        return { data: (result as any).data?.updateVerificationAttempt ?? null as VerificationAttempt | null, errors: null as any };
      },
      delete: async (keys: { id: string; startedAt?: string }) => {
        const result = await getClient().graphql({ query: deleteVerificationAttempt, variables: keys });
        return { data: (result as any).data?.deleteVerificationAttempt ?? null as VerificationAttempt | null, errors: null as any };
      },
      attemptsByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: attemptsByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.attemptsByUser;
        return { data: (conn?.items ?? []) as VerificationAttempt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      attemptsByDocHash: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: attemptsByDocHash, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.attemptsByDocHash;
        return { data: (conn?.items ?? []) as VerificationAttempt[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listAttemptsByUser() { return (this as any).attemptsByUser; },
      get listAttemptsByDocHash() { return (this as any).attemptsByDocHash; },
    },
    Horn: {
      get: async (keys: { id: string; postedAt?: string }) => {
        const result = await getClient().graphql({ query: getHorn, variables: keys });
        return { data: (result as any).data?.getHorn ?? null as Horn | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listHorns, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listHorns;
        return { data: (conn?.items ?? []) as Horn[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createHorn, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createHorn ?? null as Horn | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateHorn, variables: { input: input } });
        return { data: (result as any).data?.updateHorn ?? null as Horn | null, errors: null as any };
      },
      delete: async (keys: { id: string; postedAt?: string }) => {
        const result = await getClient().graphql({ query: deleteHorn, variables: keys });
        return { data: (result as any).data?.deleteHorn ?? null as Horn | null, errors: null as any };
      },
      listHornsByCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: listHornsByCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.listHornsByCreator;
        return { data: (conn?.items ?? []) as Horn[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      hornsByFeedExpiry: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: hornsByFeedExpiry, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.hornsByFeedExpiry;
        return { data: (conn?.items ?? []) as Horn[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      hornsByProfileExpiry: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: hornsByProfileExpiry, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.hornsByProfileExpiry;
        return { data: (conn?.items ?? []) as Horn[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      hornsByVisibility: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: hornsByVisibility, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.hornsByVisibility;
        return { data: (conn?.items ?? []) as Horn[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listListHornsByCreator() { return (this as any).listHornsByCreator; },
      get listHornsByFeedExpiry() { return (this as any).hornsByFeedExpiry; },
      get listHornsByProfileExpiry() { return (this as any).hornsByProfileExpiry; },
      get listHornsByVisibility() { return (this as any).hornsByVisibility; },
    },
    HornEngagement: {
      get: async (keys: { id: string; eventAt?: string }) => {
        const result = await getClient().graphql({ query: getHornEngagement, variables: keys });
        return { data: (result as any).data?.getHornEngagement ?? null as HornEngagement | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listHornEngagements, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listHornEngagements;
        return { data: (conn?.items ?? []) as HornEngagement[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createHornEngagement, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createHornEngagement ?? null as HornEngagement | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateHornEngagement, variables: { input: input } });
        return { data: (result as any).data?.updateHornEngagement ?? null as HornEngagement | null, errors: null as any };
      },
      delete: async (keys: { id: string; eventAt?: string }) => {
        const result = await getClient().graphql({ query: deleteHornEngagement, variables: keys });
        return { data: (result as any).data?.deleteHornEngagement ?? null as HornEngagement | null, errors: null as any };
      },
      engagementsByHorn: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: engagementsByHorn, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.engagementsByHorn;
        return { data: (conn?.items ?? []) as HornEngagement[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      engagementsByViewer: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: engagementsByViewer, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.engagementsByViewer;
        return { data: (conn?.items ?? []) as HornEngagement[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listEngagementsByHorn() { return (this as any).engagementsByHorn; },
      get listEngagementsByViewer() { return (this as any).engagementsByViewer; },
    },
    HornComment: {
      get: async (keys: { id: string; commentedAt?: string }) => {
        const result = await getClient().graphql({ query: getHornComment, variables: keys });
        return { data: (result as any).data?.getHornComment ?? null as HornComment | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listHornComments, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listHornComments;
        return { data: (conn?.items ?? []) as HornComment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createHornComment, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createHornComment ?? null as HornComment | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateHornComment, variables: { input: input } });
        return { data: (result as any).data?.updateHornComment ?? null as HornComment | null, errors: null as any };
      },
      delete: async (keys: { id: string; commentedAt?: string }) => {
        const result = await getClient().graphql({ query: deleteHornComment, variables: keys });
        return { data: (result as any).data?.deleteHornComment ?? null as HornComment | null, errors: null as any };
      },
      commentsByHorn: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: commentsByHorn, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.commentsByHorn;
        return { data: (conn?.items ?? []) as HornComment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      commentRepliesByHorn: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: commentRepliesByHorn, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.commentRepliesByHorn;
        return { data: (conn?.items ?? []) as HornComment[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },

      get listCommentsByHorn() { return (this as any).commentsByHorn; },
    },
    HornRepost: {
      get: async (keys: { reposterUserId: string; originalHornId: string }) => {
        const result = await getClient().graphql({ query: getHornRepost, variables: keys });
        return { data: (result as any).data?.getHornRepost ?? null as HornRepost | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listHornReposts, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listHornReposts;
        return { data: (conn?.items ?? []) as HornRepost[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createHornRepost, variables: { input } });
        return { data: (result as any).data?.createHornRepost ?? null as HornRepost | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateHornRepost, variables: { input } });
        return { data: (result as any).data?.updateHornRepost ?? null as HornRepost | null, errors: null as any };
      },
      delete: async (keys: { reposterUserId: string; originalHornId: string }) => {
        const result = await getClient().graphql({ query: deleteHornRepost, variables: keys });
        return { data: (result as any).data?.deleteHornRepost ?? null as HornRepost | null, errors: null as any };
      },
      repostsByReposter: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: repostsByReposter, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.repostsByReposter;
        return { data: (conn?.items ?? []) as HornRepost[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      repostsOfHorn: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: repostsOfHorn, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.repostsOfHorn;
        return { data: (conn?.items ?? []) as HornRepost[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      repostsByOriginalCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: repostsByOriginalCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.repostsByOriginalCreator;
        return { data: (conn?.items ?? []) as HornRepost[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },

      get listRepostsByReposter() { return (this as any).repostsByReposter; },
      get listRepostsOfHorn() { return (this as any).repostsOfHorn; },
      get listRepostsByOriginalCreator() { return (this as any).repostsByOriginalCreator; },
    },
    PostShare: {
      get: async (keys: { sharerUserId: string; originalPostId: string }) => {
        const result = await getClient().graphql({ query: getPostShare, variables: keys });
        return { data: (result as any).data?.getPostShare ?? null as PostShare | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listPostShares, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listPostShares;
        return { data: (conn?.items ?? []) as PostShare[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createPostShare, variables: { input } });
        return { data: (result as any).data?.createPostShare ?? null as PostShare | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updatePostShare, variables: { input } });
        return { data: (result as any).data?.updatePostShare ?? null as PostShare | null, errors: null as any };
      },
      delete: async (keys: { sharerUserId: string; originalPostId: string }) => {
        const result = await getClient().graphql({ query: deletePostShare, variables: keys });
        return { data: (result as any).data?.deletePostShare ?? null as PostShare | null, errors: null as any };
      },
      sharesBySharer: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: sharesBySharer, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.sharesBySharer;
        return { data: (conn?.items ?? []) as PostShare[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      sharesOfPost: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: sharesOfPost, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.sharesOfPost;
        return { data: (conn?.items ?? []) as PostShare[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      sharesByOriginalCreator: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: sharesByOriginalCreator, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.sharesByOriginalCreator;
        return { data: (conn?.items ?? []) as PostShare[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },

      get listSharesBySharer() { return (this as any).sharesBySharer; },
      get listSharesOfPost() { return (this as any).sharesOfPost; },
      get listSharesByOriginalCreator() { return (this as any).sharesByOriginalCreator; },
    },
    MFABackupCode: {
      get: async (keys: { userId: string; codeHash: string }) => {
        const result = await getClient().graphql({ query: getMFABackupCode, variables: keys });
        return { data: (result as any).data?.getMFABackupCode ?? null as MFABackupCode | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listMFABackupCodes, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listMFABackupCodes;
        return { data: (conn?.items ?? []) as MFABackupCode[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createMFABackupCode, variables: { input } });
        return { data: (result as any).data?.createMFABackupCode ?? null as MFABackupCode | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateMFABackupCode, variables: { input } });
        return { data: (result as any).data?.updateMFABackupCode ?? null as MFABackupCode | null, errors: null as any };
      },
      delete: async (keys: { userId: string; codeHash: string }) => {
        const result = await getClient().graphql({ query: deleteMFABackupCode, variables: keys });
        return { data: (result as any).data?.deleteMFABackupCode ?? null as MFABackupCode | null, errors: null as any };
      },
      backupCodesByUserId: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: backupCodesByUserId, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.backupCodesByUserId;
        return { data: (conn?.items ?? []) as MFABackupCode[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },

      get listBackupCodesByUserId() { return (this as any).backupCodesByUserId; },
    },
    Feedback: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getFeedback, variables: { id: id } });
        return { data: (result as any).data?.getFeedback ?? null as Feedback | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listFeedbacks, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listFeedbacks;
        return { data: (conn?.items ?? []) as Feedback[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createFeedback, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createFeedback ?? null as Feedback | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateFeedback, variables: { input: input } });
        return { data: (result as any).data?.updateFeedback ?? null as Feedback | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteFeedback, variables: { id: id } });
        return { data: (result as any).data?.deleteFeedback ?? null as Feedback | null, errors: null as any };
      },
    },
    TesterSignup: {
      get: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: getTesterSignup, variables: { id: id } });
        return { data: (result as any).data?.getTesterSignup ?? null as TesterSignup | null, errors: null as any };
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: listTesterSignups, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } });
        const conn = (result as any).data?.listTesterSignups;
        return { data: (conn?.items ?? []) as TesterSignup[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      create: async (input: any) => {
        const result = await getClient().graphql({ query: createTesterSignup, variables: { input: ensureId(input) } });
        return { data: (result as any).data?.createTesterSignup ?? null as TesterSignup | null, errors: null as any };
      },
      update: async (input: any) => {
        const result = await getClient().graphql({ query: updateTesterSignup, variables: { input: input } });
        return { data: (result as any).data?.updateTesterSignup ?? null as TesterSignup | null, errors: null as any };
      },
      delete: async (idOrObj: string | { id: string }) => {
        const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
        const result = await getClient().graphql({ query: deleteTesterSignup, variables: { id: id } });
        return { data: (result as any).data?.deleteTesterSignup ?? null as TesterSignup | null, errors: null as any };
      },
      testerSignupByStatus: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: testerSignupByStatus, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.testerSignupByStatus;
        return { data: (conn?.items ?? []) as TesterSignup[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    
      get listTesterSignupByStatus() { return (this as any).testerSignupByStatus; },
    },
    // ========== StoryHighlight ==========
    StoryHighlight: {
      get: async (idOrObj: string | { id: string }) => {
        try {
          const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
          const result = await getClient().graphql({ query: ops.getStoryHighlight, variables: { id: id } }) as any;
          if (result.errors?.length > 0) {
            console.error('[cdkModelsClient] StoryHighlight.get errors:', result.errors);
            return { data: null as StoryHighlight | null, errors: result.errors };
          }
          return { data: result.data?.getStoryHighlight ?? null as StoryHighlight | null, errors: null as any };
        } catch (err: any) {
          console.error('[cdkModelsClient] StoryHighlight.get threw:', err);
          return { data: null as StoryHighlight | null, errors: err.errors || [err] };
        }
      },
      list: async (opts?: { filter?: any; limit?: number; nextToken?: string }) => {
        try {
          const result = await getClient().graphql({ query: ops.listStoryHighlights, variables: { filter: opts?.filter ? JSON.stringify(opts.filter) : undefined, limit: opts?.limit, nextToken: opts?.nextToken } }) as any;
          if (result.errors?.length > 0) {
            console.error('[cdkModelsClient] StoryHighlight.list errors:', result.errors);
            return { data: [] as StoryHighlight[], nextToken: null, errors: result.errors };
          }
          const conn = result.data?.listStoryHighlights;
          return { data: (conn?.items ?? []) as StoryHighlight[], nextToken: conn?.nextToken ?? null, errors: null as any };
        } catch (err: any) {
          console.error('[cdkModelsClient] StoryHighlight.list threw:', err);
          return { data: [] as StoryHighlight[], nextToken: null, errors: err.errors || [err] };
        }
      },
      create: async (input: any) => {
        try {
          console.log('[cdkModelsClient] StoryHighlight.create called with:', input);
          const result = await getClient().graphql({ query: ops.createStoryHighlight, variables: { input: ensureId(input) } }) as any;
          console.log('[cdkModelsClient] StoryHighlight.create raw result:', JSON.stringify(result, null, 2));
          if (result.errors?.length > 0) {
            console.error('[cdkModelsClient] StoryHighlight.create errors:', result.errors);
            return { data: null as StoryHighlight | null, errors: result.errors };
          }
          return { data: result.data?.createStoryHighlight ?? null as StoryHighlight | null, errors: null as any };
        } catch (err: any) {
          console.error('[cdkModelsClient] StoryHighlight.create threw:', err);
          return { data: null as StoryHighlight | null, errors: err.errors || [err] };
        }
      },
      update: async (input: any) => {
        try {
          console.log('[cdkModelsClient] StoryHighlight.update called with:', input);
          const result = await getClient().graphql({ query: ops.updateStoryHighlight, variables: { input: input } }) as any;
          console.log('[cdkModelsClient] StoryHighlight.update raw result:', JSON.stringify(result, null, 2));
          if (result.errors?.length > 0) {
            console.error('[cdkModelsClient] StoryHighlight.update errors:', result.errors);
            return { data: null as StoryHighlight | null, errors: result.errors };
          }
          return { data: result.data?.updateStoryHighlight ?? null as StoryHighlight | null, errors: null as any };
        } catch (err: any) {
          console.error('[cdkModelsClient] StoryHighlight.update threw:', err);
          return { data: null as StoryHighlight | null, errors: err.errors || [err] };
        }
      },
      delete: async (idOrObj: string | { id: string }) => {
        try {
          const id = typeof idOrObj === 'string' ? idOrObj : idOrObj.id;
          const result = await getClient().graphql({ query: ops.deleteStoryHighlight, variables: { id: id } }) as any;
          if (result.errors?.length > 0) {
            console.error('[cdkModelsClient] StoryHighlight.delete errors:', result.errors);
            return { data: null as StoryHighlight | null, errors: result.errors };
          }
          return { data: result.data?.deleteStoryHighlight ?? null as StoryHighlight | null, errors: null as any };
        } catch (err: any) {
          console.error('[cdkModelsClient] StoryHighlight.delete threw:', err);
          return { data: null as StoryHighlight | null, errors: err.errors || [err] };
        }
      },
      highlightsByUser: async (...args: any[]) => {
        try {
          const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
          console.log('[cdkModelsClient] StoryHighlight.highlightsByUser called with:', opts);
          const result = await getClient().graphql({ query: ops.highlightsByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } }) as any;
          console.log('[cdkModelsClient] StoryHighlight.highlightsByUser raw result:', JSON.stringify(result, null, 2));
          if (result.errors?.length > 0) {
            console.error('[cdkModelsClient] StoryHighlight.highlightsByUser errors:', result.errors);
            return { data: [] as StoryHighlight[], nextToken: null, errors: result.errors };
          }
          const conn = result.data?.highlightsByUser;
          return { data: (conn?.items ?? []) as StoryHighlight[], nextToken: conn?.nextToken ?? null, errors: null as any };
        } catch (err: any) {
          console.error('[cdkModelsClient] StoryHighlight.highlightsByUser threw:', err);
          return { data: [] as StoryHighlight[], nextToken: null, errors: err.errors || [err] };
        }
      },
    },

    // ========== CommentLike ==========
    CommentLike: {
      get: (keys: Pick<CommentLike, 'id'>) =>
        gqlOp<CommentLike>(getCommentLike, keys),
      create: (input: Partial<CommentLike> & Pick<CommentLike, 'commentId' | 'commentType' | 'likerUserId' | 'userCommentKey'>) =>
        gqlOp<CommentLike>(createCommentLike, { input: ensureId(input) }),
      update: (input: Partial<CommentLike> & Pick<CommentLike, 'id'>) =>
        gqlOp<CommentLike>(updateCommentLike, { input }),
      delete: (keys: Pick<CommentLike, 'id'>) =>
        gqlOp<CommentLike>(deleteCommentLike, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<CommentLike>(listCommentLikes, opts || {}),
      likesByComment: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: likesByComment, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.likesByComment;
        return { data: (conn?.items ?? []) as CommentLike[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      likesByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: likesByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.likesByUser;
        return { data: (conn?.items ?? []) as CommentLike[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    },

    // ========== SavedContent ==========
    SavedContent: {
      get: (keys: Pick<SavedContent, 'id'>) =>
        gqlOp<SavedContent>(getSavedContent, keys),
      create: (input: Partial<SavedContent> & Pick<SavedContent, 'userId' | 'contentType' | 'contentId' | 'userContentKey' | 'savedAt'>) =>
        gqlOp<SavedContent>(createSavedContent, { input: ensureId(input) }),
      update: (input: Partial<SavedContent> & Pick<SavedContent, 'id'>) =>
        gqlOp<SavedContent>(updateSavedContent, { input }),
      delete: (keys: Pick<SavedContent, 'id'>) =>
        gqlOp<SavedContent>(deleteSavedContent, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<SavedContent>(listSavedContents, opts || {}),
      savedContentByUser: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: savedContentByUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.savedContentByUser;
        return { data: (conn?.items ?? []) as SavedContent[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    },

    // ========== PostHashtag ==========
    PostHashtag: {
      get: (keys: Pick<PostHashtag, 'id'>) =>
        gqlOp<PostHashtag>(getPostHashtag, keys),
      create: (input: Partial<PostHashtag> & Pick<PostHashtag, 'hashtag' | 'postId' | 'creatorUserId' | 'publishedAt'>) =>
        gqlOp<PostHashtag>(createPostHashtag, { input: ensureId(input) }),
      update: (input: Partial<PostHashtag> & Pick<PostHashtag, 'id'>) =>
        gqlOp<PostHashtag>(updatePostHashtag, { input }),
      delete: (keys: Pick<PostHashtag, 'id'>) =>
        gqlOp<PostHashtag>(deletePostHashtag, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<PostHashtag>(listPostHashtags, opts || {}),
      postsByHashtag: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: postsByHashtag, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.postsByHashtag;
        return { data: (conn?.items ?? []) as PostHashtag[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    },

    // ========== HornHashtag ==========
    HornHashtag: {
      get: (keys: Pick<HornHashtag, 'id'>) =>
        gqlOp<HornHashtag>(getHornHashtag, keys),
      create: (input: Partial<HornHashtag> & Pick<HornHashtag, 'hashtag' | 'hornId' | 'creatorUserId' | 'publishedAt'>) =>
        gqlOp<HornHashtag>(createHornHashtag, { input: ensureId(input) }),
      update: (input: Partial<HornHashtag> & Pick<HornHashtag, 'id'>) =>
        gqlOp<HornHashtag>(updateHornHashtag, { input }),
      delete: (keys: Pick<HornHashtag, 'id'>) =>
        gqlOp<HornHashtag>(deleteHornHashtag, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<HornHashtag>(listHornHashtags, opts || {}),
      hornsByHashtag: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: hornsByHashtag, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.hornsByHashtag;
        return { data: (conn?.items ?? []) as HornHashtag[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    },
    FollowRequest: {
      get: (keys: Pick<FollowRequest, 'id'>) =>
        gqlOp<FollowRequest>(getFollowRequest, keys),
      create: (input: Omit<FollowRequest, 'createdAt' | 'updatedAt'>) =>
        gqlOp<FollowRequest>(createFollowRequest, { input: ensureId(input as any) }),
      update: (input: Partial<FollowRequest> & Pick<FollowRequest, 'id'>) =>
        gqlOp<FollowRequest>(updateFollowRequest, { input }),
      delete: (keys: Pick<FollowRequest, 'id'>) =>
        gqlOp<FollowRequest>(deleteFollowRequest, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<FollowRequest>(listFollowRequests, opts || {}),
      requestsByTarget: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: requestsByTarget, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.requestsByTarget;
        return { data: (conn?.items ?? []) as FollowRequest[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      get listRequestsByTarget() { return (this as any).requestsByTarget; },
      requestsByRequester: async (...args: any[]) => {
        const opts = args.length === 2 ? { ...args[0], ...args[1] } : args[0] ?? {};
        const result = await getClient().graphql({ query: requestsByRequester, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.requestsByRequester;
        return { data: (conn?.items ?? []) as FollowRequest[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
      get listRequestsByRequester() { return (this as any).requestsByRequester; },
    },
    ContentScore: {
      get: (keys: Pick<ContentScore, 'id'>) =>
        gqlOp<ContentScore>(getContentScore, keys),
      create: (input: Partial<ContentScore> & Pick<ContentScore, 'id' | 'contentType' | 'contentId' | 'score' | 'computedAt'>) =>
        gqlOp<ContentScore>(createContentScore, { input }),
      update: (input: Partial<ContentScore> & Pick<ContentScore, 'id'>) =>
        gqlOp<ContentScore>(updateContentScore, { input }),
      delete: (keys: Pick<ContentScore, 'id'>) =>
        gqlOp<ContentScore>(deleteContentScore, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<ContentScore>(listContentScores, opts || {}),
      scoresByContentType: async (opts: { contentType: string; score?: number; sortDirection?: string; filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: scoresByContentType, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.scoresByContentType;
        return { data: (conn?.items ?? []) as ContentScore[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    },
    SuggestedFollow: {
      get: (keys: Pick<SuggestedFollow, 'id'>) =>
        gqlOp<SuggestedFollow>(getSuggestedFollow, keys),
      create: (input: Partial<SuggestedFollow> & Pick<SuggestedFollow, 'id' | 'targetUserId' | 'suggestedUserId' | 'score' | 'reason' | 'computedAt'>) =>
        gqlOp<SuggestedFollow>(createSuggestedFollow, { input }),
      update: (input: Partial<SuggestedFollow> & Pick<SuggestedFollow, 'id'>) =>
        gqlOp<SuggestedFollow>(updateSuggestedFollow, { input }),
      delete: (keys: Pick<SuggestedFollow, 'id'>) =>
        gqlOp<SuggestedFollow>(deleteSuggestedFollow, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<SuggestedFollow>(listSuggestedFollows, opts || {}),
      suggestionsByTargetUser: async (opts: { targetUserId: string; score?: number; sortDirection?: string; filter?: any; limit?: number; nextToken?: string }) => {
        const result = await getClient().graphql({ query: suggestionsByTargetUser, variables: { ...opts, filter: opts.filter ? JSON.stringify(opts.filter) : undefined } });
        const conn = (result as any).data?.suggestionsByTargetUser;
        return { data: (conn?.items ?? []) as SuggestedFollow[], nextToken: conn?.nextToken ?? null, errors: null as any };
      },
    },

    // ========== TemporaryBannerMessage ==========
    TemporaryBannerMessage: {
      get: (keys: Pick<TemporaryBannerMessage, 'id'>) =>
        gqlOp<TemporaryBannerMessage>(getTemporaryBannerMessage, keys),
      create: (input: Partial<TemporaryBannerMessage> & Pick<TemporaryBannerMessage, 'message' | 'isEnabled'>) =>
        gqlOp<TemporaryBannerMessage>(createTemporaryBannerMessage, { input: ensureId(input) }),
      update: (input: Partial<TemporaryBannerMessage> & Pick<TemporaryBannerMessage, 'id'>) =>
        gqlOp<TemporaryBannerMessage>(updateTemporaryBannerMessage, { input }),
      delete: (keys: Pick<TemporaryBannerMessage, 'id'>) =>
        gqlOp<TemporaryBannerMessage>(deleteTemporaryBannerMessage, keys),
      list: (opts?: { filter?: any; limit?: number; nextToken?: string }) =>
        gqlList<TemporaryBannerMessage>(listTemporaryBannerMessages, opts || {}),
    },
  },
};
